// ============================================
// PanelFlujo.js — vista de paso del expediente (FLUJOS FIJOS, Entrega 1)
// ============================================
// Pieza central de la ficha: muestra el flujo completo (stepper), el paso
// actual con su requisito ("Falta: ..."), y el boton de accion que lo
// resuelve. Los pasos SALTABLES muestran "Saltar este paso" (queda
// registrado); los CONDICIONALES solo aparecen si su condicion se dio.
// Al cumplirse el requisito el expediente AVANZA solo (backend motorFlujos)
// y este panel refresca + avisa con un toast.
//
// Backend: GET/POST /api/oportunidades/:id/flujo* y /pagos (src/routes/flujos.js)

// ALMACEN v3 Parte B (2026-10-21): piezas del AUDITOR enganchadas a los pasos que ya existen.
import { CobroBanner, MapaResumen, ActaEntregaModal, CuadrePanel } from './AuditorEnFlujo';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  API_BASE_CLI, FF,
  COLOR_BEEP_AZUL, COLOR_OSCURO, COLOR_BORDE, COLOR_GRIS, COLOR_GRIS_CL,
  COLOR_OK, COLOR_PELIGRO,
  authHeaders, inputStyle, botonPrimario, botonSecundario, ShellModal, ToastFlotante,
} from './helpersExpedientes';
import { ACCIONES, TIPOS_TRABAJO, PASO_VISUAL } from '../../utils/flujos';
// MOVIL-2: iconos por paso en la vista de flujo de celular.
import { IconoLucide } from '../SelectorIconoLucide';
// PANEL-NAVEGADOR: el contexto del proyecto (paso 1) se captura DENTRO del
// panel unico del paso, no en una seccion aparte ni en modales.
import ContextoProyecto from './ContextoProyecto';
// ESPEJO-COTIZACIONES (2026-07-10): el panel del paso Cotizacion muestra EL
// MISMO componente que la pestaña Cotizaciones (dashboard + lista + escanear).
import CotizacionesLista from '../CotizacionesLista';
// COTIZAR-RAPIDO (2026-09-23): el paso de Cotizacion decia 'Falta: Vincular una
// cotizacion al expediente' y no ofrecia ninguna forma de hacerlo. Ahora si.
import PanelCotizarRapido from './PanelCotizarRapido';
import CotizacionesParecidas from './CotizacionesParecidas';
import LevantamientoForm from './LevantamientoForm';
import { ProgramarDesdeExpediente } from '../../CalendarioCampo';
import {
  Check, ChevronRight, ChevronDown, SkipForward, AlertCircle, RefreshCw, Lock,
  CircleDollarSign, PartyPopper, Save, Undo2, AlertTriangle,
  CircleDot,
} from 'lucide-react';

// TAREAS-HORIZONTAL (2026-07-13): se retiraron las etapas visuales
// (Comercial/Material/Ejecucion/Cierre) y el checklist vertical; el flujo
// horizontal de nodos comunica el avance. Constantes ETAPA_DE_PASO/
// ETAPAS_ORDEN eliminadas por quedar sin uso.

// TAREAS-EXPEDIENTE (2026-07-10): accion de registro que COMPLETA cada
// requisito (para llenar despues un paso saltado desde su detalle).
const REQUISITO_ACCION = {
  registro_visita: 'registrar_visita',
  registro_falla: 'registrar_falla',
  registro_solicitud: 'registrar_solicitud',
  registro_diagnostico: 'registrar_diagnostico',
  registro_trabajo: 'registrar_trabajo',
  registro_reparacion: 'registrar_reparacion',
  registro_salida: 'registrar_salida',
  registro_factura: 'registrar_factura',
  registro_entrega: 'registrar_entrega',
  // La entrega pide conformidad + cobro: su registro es la entrega (el cobro
  // va aparte por el endpoint de pagos, con sus candados).
  entrega_cobro: 'registrar_entrega',
};

// PANEL-NAVEGADOR: preview humano de que pedira cada paso (para pasos futuros).
// Espejo de los textos del motor (src/services/motorFlujos.js REQUISITOS).
const REQUISITO_TEXTO = {
  cliente_necesidad: 'Identificar al cliente y capturar que necesita (contexto del proyecto)',
  registro_visita: 'Registrar el levantamiento (o marcarlo listo / saltarlo)',
  registro_falla: 'Registrar la falla que reporta el cliente',
  registro_solicitud: 'Registrar que esta pidiendo el cliente',
  registro_diagnostico: 'Registrar el diagnostico del equipo',
  cotizacion_autorizada: 'Vincular una cotizacion al expediente y autorizarla',
  pago_anticipo: 'Registrar el anticipo del cliente',
  pago_total: 'Registrar el pago total (100%, regla de Ventas)',
  material_checklist: 'Revisar el checklist de material con almacen',
  material_checklist_salida: 'Checklist de material y salida de almacen',
  faltantes_recibidos: 'Traer del almacen los faltantes (cuando la compra ya entro)',
  registro_salida: 'Registrar la salida del material de almacen',
  registro_trabajo: 'Marcar el trabajo de instalacion como terminado',
  registro_reparacion: 'Marcar el servicio como terminado',
  entrega_cobro: 'Confirmar la entrega con el cliente y cobrar el restante',
  registro_factura: 'Registrar la factura emitida con su folio',
  registro_entrega: 'Confirmar la entrega con el cliente',
};

// PASOS-NAVEGABLES (2026-07-09): etiqueta humana de cada registro capturado
// en un paso (para el detalle del paso en la linea de tiempo).
const REGISTRO_LABELS = {
  visita: 'Notas de la visita',
  falla: 'Falla reportada',
  solicitud: 'Solicitud del cliente',
  diagnostico: 'Diagnostico',
  trabajo: 'Trabajo terminado',
  reparacion: 'Servicio terminado',
  salida: 'Salida de material',
  entrega: 'Entrega confirmada',
  factura: 'Factura emitida',
};
// Registros de sistema que NO se listan como "informacion del paso".
const REGISTROS_SISTEMA = [
  'recordatorio_responsable', 'reasignacion', 'edicion',
  'impresion_portada', 'edicion_retroactiva', 'regreso_paso',
];

const TIPOS_PAGO = [
  { v: 'anticipo',  label: 'Anticipo' },
  { v: 'pago',      label: 'Pago' },
  { v: 'finiquito', label: 'Finiquito' },
];

const fmtMonto = (n) => Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// PANEL-NAVEGADOR: expediente/historial vienen de la ficha; verPasoReq
// {codigo, n} es una peticion externa de navegar a un paso (ej. "Editar
// contexto" desde la card de Resumen).
export default function PanelFlujo({ expedienteId, esCelular, direccion, verFinanzas = true, cerradoManual, onActualizado, onVerCotizaciones, onEditarExpediente, expediente, historial, verPasoReq }) {
  const [flujo, setFlujo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { tipo: 'checklist'|'saltar'|'faltantes'|'confirmar_tipo', ... }
  const [toast, setToast] = useState(null);
  const [panelCotizar, setPanelCotizar] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const [actaAbierta, setActaAbierta] = useState(false); // AUDITOR: acta de entrega (B8)
  // CALENDARIO-E2: agendar desde el propio expediente (paso Levantamiento y otros).
  const [programarVisita, setProgramarVisita] = useState(false);
  const stepperRef = useRef(null);
  // PANEL-NAVEGADOR: paso que se esta VIENDO en el panel unico (distinto del
  // paso ACTUAL del expediente). Al abrir, el panel muestra el actual.
  const [pasoVisto, setPasoVisto] = useState(null);
  const [expand, setExpand] = useState(null); // null | 'saltar_a' | 'regresar' | 'pago_extra'
  // PASOS-ABIERTOS (2026-07-13): los pasos que capturan info se muestran SIEMPRE
  // con su formulario abierto y editable (sin resumen cerrado + boton Editar).
  const panelRef = useRef(null);
  // ETAPAS-VISUAL: contenedor del checklist lateral (para enfocar un paso al
  // picar una tarjeta de etapa).
  const checklistRef = useRef(null);

  // Navegar el panel a un paso (scroll suave SOLO en celular; block nearest
  // para no provocar brincos — leccion de scroll del proyecto).
  const verPaso = useCallback((codigo) => {
    setPasoVisto(codigo);
    setExpand(null);
    if (esCelular && panelRef.current && typeof panelRef.current.scrollIntoView === 'function') {
      try { panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch { /* safari viejo */ }
    }
  }, [esCelular]);

  const avisar = useCallback((msg, tipo = 'ok') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3800);
  }, []);

  const cargar = useCallback(async () => {
    try {
      setError(null);
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expedienteId}/flujo`, { headers: authHeaders() })
        .then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo cargar el flujo');
      setFlujo(r);
      return r; // ESPEJO: permite comparar el paso actual antes/despues de refrescar
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [expedienteId]);

  useEffect(() => { setCargando(true); cargar(); }, [cargar]);

  // Centrar el paso actual en el flujo al cargar (chips en movil, fila
  // horizontal en desktop — el que este montado).
  useEffect(() => {
    if (!flujo) return;
    const cont = stepperRef.current || checklistRef.current;
    if (!cont) return;
    const actual = cont.querySelector('[data-actual="1"]');
    if (actual && typeof actual.scrollIntoView === 'function') {
      try { actual.scrollIntoView({ inline: 'center', block: 'nearest' }); } catch { /* safari viejo */ }
    }
  }, [flujo]);

  // PANEL-NAVEGADOR: al cargar (o si el paso visto ya no existe en el flujo),
  // el panel muestra el paso ACTUAL del expediente.
  useEffect(() => {
    if (!flujo) return;
    const codigos = (flujo.pasos || []).map(p => p.codigo);
    if (!pasoVisto || !codigos.includes(pasoVisto)) {
      setPasoVisto(flujo.actual ? flujo.actual.codigo : (codigos[0] || null));
    }
  }, [flujo, pasoVisto]);

  // Peticion externa de navegar a un paso (ej. abrir el contexto desde la ficha).
  // El paso ya se muestra abierto y editable, no hay que "entrar en modo edicion".
  useEffect(() => {
    if (verPasoReq && verPasoReq.codigo) {
      verPaso(verPasoReq.codigo);
    }
  }, [verPasoReq, verPaso]);

  // Procesa la respuesta de una accion: refresca, avisa avances y notifica a la ficha.
  const trasAccion = useCallback(async (json) => {
    await cargar();
    if (typeof onActualizado === 'function') onActualizado();
    const avances = (json && json.avances) || [];
    if (avances.length > 0) {
      const ultimo = avances[avances.length - 1];
      // PANEL-NAVEGADOR: el panel sigue al expediente cuando avanza.
      setPasoVisto(ultimo.a);
      setExpand(null);
      if (ultimo.a === 'cerrado') {
        avisar('Flujo completado: el expediente se cerro como GANADO');
      } else {
        avisar(`Listo. El expediente avanzo al paso "${nombrePaso(ultimo.a)}"`);
      }
    } else {
      avisar('Guardado');
    }
  }, [cargar, onActualizado, avisar]);

  const nombrePaso = (codigo) => {
    const p = flujo && flujo.pasos ? flujo.pasos.find(x => x.codigo === codigo) : null;
    return p ? p.nombre : codigo;
  };

  // ESPEJO: tras una accion en la lista de cotizaciones embebida (autorizar,
  // duplicar, escanear), refresca y — si el flujo avanzo solo (hook backend al
  // autorizar) — avisa con el mismo toast que trasAccion y sigue al expediente.
  const trasCambioCotizaciones = useCallback(async () => {
    const antes = flujo && flujo.actual ? flujo.actual.codigo : null;
    const r = await cargar();
    if (typeof onActualizado === 'function') onActualizado();
    const despues = r && r.actual ? r.actual.codigo : null;
    if (despues && antes && despues !== antes) {
      setPasoVisto(despues);
      if (despues === 'cerrado') {
        avisar('Flujo completado: el expediente se cerro como GANADO');
      } else {
        const p = (r.pasos || []).find(x => x.codigo === despues);
        avisar(`Listo. El expediente avanzo al paso "${p ? p.nombre : despues}"`);
      }
    }
  }, [flujo, cargar, onActualizado, avisar]);

  const llamar = useCallback(async (metodo, ruta, body) => {
    setTrabajando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}${ruta}`, {
        method: metodo,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: body ? JSON.stringify(body) : undefined,
      }).then(x => x.json());
      if (!r.ok && r.error) throw new Error(r.error);
      return r;
    } finally {
      setTrabajando(false);
    }
  }, []);

  // ===== Acciones =====
  const ejecutarAccion = async (accion) => {
    if (accion === 'editar_expediente') {
      if (typeof onEditarExpediente === 'function') onEditarExpediente();
      return;
    }
    if (accion === 'ver_cotizaciones') {
      if (typeof onVerCotizaciones === 'function') onVerCotizaciones();
      return;
    }
    if (accion === 'continuar') {
      try {
        const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/continuar`);
        await trasAccion(r);
      } catch (e) { avisar(e.message, 'error'); }
      return;
    }
    // DOS-TIEMPOS: el paso 1 puede declararse listo SIN contexto (firmado).
    if (accion === 'marcar_listo') {
      try {
        const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/continuar`, { marcar_listo: true });
        await trasAccion(r);
      } catch (e) { avisar(e.message, 'error'); }
      return;
    }
    if (accion === 'enviar_almacen') {
      if (!flujo.cotizacion) { avisar('Primero se necesita una cotizacion autorizada', 'error'); return; }
      try {
        const r = await llamar('POST', `/api/solicitudes-material/desde-cotizacion/${flujo.cotizacion.id}`);
        await cargar();
        if (typeof onActualizado === 'function') onActualizado();
        avisar(r.ya_existia ? 'La solicitud de material ya existia' : 'Solicitud de material creada. Ahora Almacen revisa el checklist.');
      } catch (e) { avisar(e.message, 'error'); }
      return;
    }
    if (accion === 'checklist_material') {
      if (!flujo.solicitud) { avisar('Aun no hay solicitud de material', 'error'); return; }
      setModal({ tipo: 'checklist' });
      return;
    }
    // ALMACEN REARMADO (2026-10-19): los faltantes se cubren confirmando la compra (entra al almacen) y TRAYENDO
    // del almacen desde el checklist / la pestana Material; ya no hay un boton que los marque recibidos a ciegas.
    if (accion === 'faltantes_recibidos') {
      if (!flujo.solicitud) { avisar('Aun no hay solicitud de material', 'error'); return; }
      setModal({ tipo: 'checklist' });
      return;
    }
    // PANEL-NAVEGADOR: pagos y registros ya se capturan INLINE en el panel
    // del paso (FormPagoInline / FormRegistroInline); no quedan modales aqui.
  };

  const saltarPaso = async (nota) => {
    try {
      const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/saltar`, { nota });
      setModal(null);
      await trasAccion(r);
    } catch (e) { avisar(e.message, 'error'); }
  };

  // TAREAS-EXPEDIENTE: BRINCO directo a un paso adelante (nota opcional; los
  // pasos brincados sin requisito quedan ambar, completables despues).
  const saltarA = async (pasoCodigo, nota) => {
    try {
      const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/saltar-a`, { paso: pasoCodigo, nota: nota || null });
      setExpand(null);
      await cargar();
      setPasoVisto(r.a);
      if (typeof onActualizado === 'function') onActualizado();
      avisar(`Saltaste a "${r.a_nombre || nombrePaso(r.a)}". Los pasos brincados quedaron pendientes (ambar).`);
    } catch (e) { avisar(e.message, 'error'); }
  };

  // PANEL-NAVEGADOR: guardar un registro de paso (inline, sin modal). paso
  // opcional = completar un paso saltado bajo su propio paso.
  const guardarRegistro = async (accion, { nota, folio, importe, paso, datos: datosExtra }) => {
    const info = ACCIONES[accion];
    // CALENDARIO-E1: el levantamiento manda datos estructurados (datosExtra);
    // el resto usa el folio si el registro lo pide.
    // FACTURA-CON-IMPORTE (2026-09-28): el importe viaja junto al folio, en el
    // MISMO registro, para que no puedan separarse nunca.
    const datos = datosExtra || (info.pideFolio && folio
      ? { folio, ...(importe ? { importe } : {}) }
      : null);
    const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/registro`, {
      tipo: info.registro, nota: nota || null, datos,
      ...(paso ? { paso } : {}),
    });
    await trasAccion(r);
    return r;
  };

  // PANEL-NAVEGADOR: registrar un pago (inline). El endpoint conserva todas
  // sus validaciones de dinero.
  const guardarPago = async ({ tipo, monto, metodo, nota }) => {
    const r = await llamar('POST', `/api/oportunidades/${expedienteId}/pagos`, { tipo, monto, metodo, nota });
    setExpand(null);
    await trasAccion(r);
    return r;
  };

  // PASOS-NAVEGABLES: regresar el expediente a un paso anterior (motivo
  // obligatorio; el backend lo firma y lo deja en el historial).
  const regresarPaso = async (pasoCodigo, motivo) => {
    try {
      const r = await llamar('POST', `/api/oportunidades/${expedienteId}/flujo/regresar`, { paso: pasoCodigo, motivo });
      setExpand(null);
      await cargar();
      setPasoVisto(r.a);
      if (typeof onActualizado === 'function') onActualizado();
      avisar(`El expediente regreso al paso "${r.a_nombre || nombrePaso(r.a)}"`);
    } catch (e) { avisar(e.message, 'error'); }
  };

  // PASOS-NAVEGABLES: edicion retroactiva de la nota de un registro (no mueve
  // el paso; deja huella 'edicion_retroactiva' en el historial).
  const editarRegistro = async (eventoId, nota, importe) => {
    // RECUPERAR-IMPORTES (2026-09-28): la misma ruta sirve para corregir la nota
    // y para capturar el importe de una factura que se registro sin el. Toca el
    // REGISTRO, no el expediente: por eso funciona tambien en los cerrados, sin
    // reabrir nada ni mover ningun paso.
    const cuerpo = {};
    if (nota != null && String(nota).trim() !== '') cuerpo.nota = String(nota).trim();
    if (importe != null) cuerpo.importe = importe;
    const r = await llamar('PUT', `/api/oportunidades/${expedienteId}/flujo/registro/${eventoId}`, cuerpo);
    await cargar();
    if (typeof onActualizado === 'function') onActualizado();
    avisar(importe != null
      ? 'Importe de la factura guardado. Ya queda registrado cuanto valio.'
      : 'Registro corregido. La edicion quedo marcada en el historial.');
    return r;
  };

  // ============================================
  // PANEL-NAVEGADOR: panel UNICO del paso visto. Un solo lugar que cambia de
  // contenido segun el paso elegido en la linea de arriba. Sin ventanas.
  // ============================================
  const renderPanelPaso = () => {
    if (!flujo) return null;
    const pv = pasosVisibles.find(p => p.codigo === pasoVisto)
      || (actual && pasosVisibles.find(p => p.codigo === actual.codigo))
      || pasosVisibles[0];
    if (!pv) return null;

    const esActualVisto = !!(actual && pv.codigo === actual.codigo);
    const esPaso1 = pv.requisito === 'cliente_necesidad';
    const esPagoReq = ['pago_anticipo', 'pago_total', 'entrega_cobro'].includes(pv.requisito);
    const esCotReq = pv.requisito === 'cotizacion_autorizada';
    // CALENDARIO-E1: el paso Levantamiento (antes "Visita", codigo 'visita',
    // requisito registro_visita) usa su propio formulario tecnico.
    const esLevantamiento = pv.requisito === 'registro_visita';
    const bloqueado = flujo.cerrado || flujoCompletado;
    const accionRegistro = REQUISITO_ACCION[pv.requisito] || null;
    // Datos del levantamiento ya capturado (para precargar el form editable).
    // Cada guardado inserta un registro NUEVO, asi que se toma el MAS RECIENTE
    // (por creado_at, con fallback al ultimo del arreglo) — no el primero.
    const levEvento = esLevantamiento
      ? (flujo.eventos || [])
          .filter(e => e.paso_codigo === pv.codigo && e.registro_tipo === 'visita' && e.datos)
          .sort((a, b) => new Date(b.creado_at || 0) - new Date(a.creado_at || 0))[0] || null
      : null;
    const levInicial = levEvento && levEvento.datos && typeof levEvento.datos === 'object' ? levEvento.datos : null;
    const entrada = (historial || []).find(h => h.estado_nuevo === pv.codigo);
    const salida = (historial || []).find(h => h.estado_anterior === pv.codigo);
    const registros = (flujo.eventos || []).filter(e =>
      e.paso_codigo === pv.codigo &&
      ((e.tipo === 'registro' && e.registro_tipo && !REGISTROS_SISTEMA.includes(e.registro_tipo))
        || e.tipo === 'saltado' || e.tipo === 'detour')
    );
    // Con el total oculto (expediente ajeno) no hay nada que sugerir: se deja en 0 en vez de
    // restar sobre nulls y proponer una cifra inventada.
    const sugerido = (flujo.cotizacion && flujo.cotizacion.total != null && flujo.pagos_suma != null)
      ? Math.max(0, Number(flujo.cotizacion.total) - Number(flujo.pagos_suma))
      : 0;
    const evVisto = esActualVisto ? ev : null;

    const SIT = {
      hecho: { txt: 'Completado', color: COLOR_OK, bg: '#f0fdf4' },
      actual: { txt: 'En curso', color: COLOR_BEEP_AZUL, bg: '#eff6ff' },
      pendiente: { txt: 'Pendiente', color: COLOR_GRIS_CL, bg: '#f9fafb' },
      saltado: { txt: 'Saltado / incompleto', color: '#b45309', bg: '#fffbeb' },
      no_aplica: { txt: 'No aplica', color: COLOR_GRIS_CL, bg: '#f9fafb' },
    };
    const sit = SIT[pv.situacion] || SIT.pendiente;

    const fmtFH = (v) => {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) + ' ' +
        d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    };

    const destinosSalto = pasosVisibles.filter(p => p.situacion === 'pendiente' && !p.es_terminal);
    const puedeRegresar = (pv.situacion === 'hecho' || pv.situacion === 'saltado') && !bloqueado;
    const puedeSaltarAqui = pv.situacion === 'pendiente' && !pv.es_terminal && !bloqueado;

    // PASOS-ABIERTOS: el contexto (paso 1) se muestra SIEMPRE con su formulario
    // abierto y editable (rondas), con los valores capturados ya precargados —
    // igual en paso actual, completado o saltado. Sin resumen cerrado ni boton
    // "Editar": guardar-al-cambiar via el propio "Guardar" de ContextoProyecto.
    const capturaContexto = expediente && (
      <div style={{ marginTop: 10 }}>
        <ContextoProyecto
          expediente={expediente}
          esCelular={esCelular}
          onGuardado={(msg) => { avisar(msg || 'Contexto guardado'); cargar(); if (typeof onActualizado === 'function') onActualizado(); }}
        />
      </div>
    );

    return (
      <div style={{
        ...est.cajaPaso,
        marginTop: 0, // alineado con el checklist lateral (zona de trabajo)
        borderColor: pv.situacion === 'saltado' ? '#fde68a' : (esActualVisto ? '#bfdbfe' : COLOR_BORDE),
      }}>
        {/* ENCABEZADO del panel: que paso estoy viendo + su estado */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 12, fontWeight: 700, color: sit.color, background: sit.bg,
            border: `1px solid ${sit.color}33`, borderRadius: 999, padding: '4px 12px',
          }}>{sit.txt}</span>
          {entrada && (pv.situacion === 'hecho' || pv.situacion === 'saltado' || esActualVisto) && (
            <span style={{ fontSize: 12, color: COLOR_GRIS }}>Entro: {fmtFH(entrada.fecha)}</span>
          )}
          {pv.situacion === 'hecho' && salida && (
            <span style={{ fontSize: 12, color: COLOR_GRIS }}>
              Completado: {fmtFH(salida.fecha)}{salida.usuario_nombre ? ` por ${salida.usuario_nombre}` : ''}
            </span>
          )}
          {!esActualVisto && actual && !bloqueado && (
            <button
              onClick={() => verPaso(actual.codigo)}
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'transparent', border: 'none', color: COLOR_BEEP_AZUL,
                fontSize: 12.5, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
                minHeight: 44, padding: '4px 6px', whiteSpace: 'nowrap',
              }}
            >Ir al paso actual <ChevronRight size={14} /></button>
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginTop: 6 }}>
          {pv.nombre}
        </div>

        {/* ===== PASO ACTUAL: que falta + captura/accion INLINE ===== */}
        {esActualVisto && !bloqueado && (
          <>
            {evVisto && !evVisto.cumplido && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 8 }}>
                <Lock size={14} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 13.5, color: '#92400e', lineHeight: 1.45, fontWeight: 500 }}>
                  Falta: {evVisto.falta}
                </div>
              </div>
            )}
            {evVisto && evVisto.cumplido && !flujo.espera_cierre && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8 }}>
                <Check size={15} color={COLOR_OK} />
                <div style={{ fontSize: 13.5, color: '#166534', fontWeight: 500 }}>
                  Requisito cumplido: listo para avanzar.
                </div>
              </div>
            )}

            {/* CIERRE-CON-PERMISO (2026-09-28): el expediente termino su ultimo
                paso pero sigue abierto porque cerrarlo como GANADO es cosa de
                Direccion. Antes esto pasaba en silencio: quien registraba la
                factura cerraba el expediente sin saberlo. Ahora se dice, y se
                dice a todo el mundo igual (el servidor no mira quien pregunta),
                para que cualquiera sepa a quien avisar. */}
            {flujo.espera_cierre && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10,
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12,
              }}>
                <Check size={16} color="#1d4ed8" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 13, color: '#1e40af', lineHeight: 1.45 }}>
                  <strong>Todo listo: este expediente ya se puede cerrar.</strong>
                  <div style={{ marginTop: 3, fontWeight: 500 }}>
                    Cerrarlo como ganado lo hace Direccion. Avisale y quedara terminado.
                  </div>
                </div>
              </div>
            )}

            {/* Cobrado vs total (cuando hay dinero en juego) — oculto al tecnico.
                EXPEDIENTES-RESUMEN (2026-09-17): y tambien oculto cuando el expediente es de otra
                persona y este usuario no ve montos ajenos — el servidor manda `pagos_suma` en null.
                Sin esta guarda saldria "Cobrado: $0.00 de $0.00", que seria mentira: el dinero
                existe, simplemente no le toca verlo. */}
            {verFinanzas && flujo.cotizacion && flujo.pagos_suma != null && flujo.cotizacion.total != null
              && (Number(flujo.pagos_suma) > 0 || esPagoReq) && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '6px 10px', fontSize: 12.5, color: '#1e40af', fontWeight: 600 }}>
                <CircleDollarSign size={14} />
                Cobrado: ${fmtMonto(flujo.pagos_suma)} de ${fmtMonto(flujo.cotizacion.total)} ({flujo.cotizacion.folio})
              </div>
            )}

            {/* AUDITOR (ALMACEN v3 Parte B): nada de esto agrega pasos; se engancha al paso actual */}
            {esActualVisto && flujo.auditor && <CobroBanner auditor={flujo.auditor} />}
            {esActualVisto && !bloqueado && ['registro_trabajo', 'registro_reparacion'].includes(pv.requisito) && (
              <MapaResumen auditor={flujo.auditor} esServicio={pv.requisito === 'registro_reparacion'} onIrPuntos={() => { try { window.dispatchEvent(new CustomEvent('beep-expediente-tab', { detail: { tab: 'equipos' } })); } catch { /* nada */ } }} />
            )}
            {esActualVisto && !bloqueado && ['entrega_cobro', 'registro_entrega'].includes(pv.requisito) && evVisto && !evVisto.cumplido && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', background: '#eef5fd', borderRadius: 12, padding: '10px 12px' }}>
                <span style={{ fontSize: 12.5, color: '#2f5f9e', flex: 1, minWidth: 200 }}>📋 <strong>Acta de entrega:</strong> el reporte lleva el mapa de puntos (punto → serie → fotos) y lo firma el cliente con el dedo.</span>
                <button type="button" onClick={() => setActaAbierta(true)} data-auditor="acta" style={{ padding: '10px 14px', minHeight: 44, background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>✍️ Entregar con firma del cliente</button>
              </div>
            )}
            {esActualVisto && flujo.auditor && (flujo.auditor.espera_cuadre || flujo.espera_cierre || (flujo.auditor.cuadre_bloquea && ['registro_factura', 'entrega_cobro', 'registro_entrega'].includes(pv.requisito))) && (
              <CuadrePanel expedienteId={expedienteId} auditor={flujo.auditor} onCambio={() => cargar && cargar()} />
            )}
            {actaAbierta && <ActaEntregaModal expedienteId={expedienteId} onCerrar={() => setActaAbierta(false)} onHecha={(d) => { setActaAbierta(false); trasAccion && trasAccion(d); }} />}

            {/* Captura INLINE segun el requisito del paso actual */}
            {esPaso1 && capturaContexto}
            {esLevantamiento && (
              <LevantamientoForm
                valorInicial={levInicial}
                trabajando={trabajando}
                onGuardar={({ nota, datos }) => guardarRegistro('registrar_visita', { nota, datos })}
                onProgramarVisita={() => setProgramarVisita(true)}
              />
            )}
            {!esPaso1 && !esLevantamiento && accionRegistro && evVisto && !evVisto.cumplido && (
              <FormRegistroInline
                accion={accionRegistro}
                trabajando={trabajando}
                sinCotizacion={!!(flujo && flujo.dinero && flujo.dinero.sin_cotizacion)}
                onGuardar={(datos) => guardarRegistro(accionRegistro, datos)}
              />
            )}
            {esPagoReq && verFinanzas && (
              <>
                <BloquePagos pagos={flujo.pagos} fmtFH={fmtFH} />
                {evVisto && !evVisto.cumplido && (
                  <FormPagoInline sugerido={sugerido} trabajando={trabajando} onGuardar={guardarPago} />
                )}
              </>
            )}
            {esPagoReq && !verFinanzas && (
              <div style={{ marginTop: 10, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5,
                background: '#f9fafb', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12 }}>
                El pago de este expediente lo gestiona el area comercial/administracion.
              </div>
            )}

            {/* Acciones del paso actual */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              {evVisto && evVisto.cumplido && (
                <BotonAccion primario disabled={trabajando} onClick={() => ejecutarAccion('continuar')}>
                  {ACCIONES.continuar.label} <ChevronRight size={15} />
                </BotonAccion>
              )}
              {esPaso1 && evVisto && !evVisto.cumplido && (
                <>
                  {String(evVisto.falta || '').toLowerCase().includes('cliente') && (
                    <BotonAccion primario disabled={trabajando} onClick={() => ejecutarAccion('editar_expediente')}>
                      Completar datos del cliente
                    </BotonAccion>
                  )}
                  <BotonAccion disabled={trabajando} onClick={() => ejecutarAccion('marcar_listo')}>
                    Marcar listo y continuar <ChevronRight size={15} />
                  </BotonAccion>
                </>
              )}
              {/* ESPEJO-COTIZACIONES: el paso Cotizacion ya trae la lista
                  completa embebida abajo; el boton "Ver cotizaciones" sobro. */}
              {evVisto && !evVisto.cumplido && ['enviar_almacen', 'checklist_material', 'faltantes_recibidos'].includes(evVisto.accion) && (
                <BotonAccion primario disabled={trabajando} onClick={() => ejecutarAccion(evVisto.accion)}>
                  {ACCIONES[evVisto.accion].label}
                </BotonAccion>
              )}
              {actual.saltable && evVisto && !evVisto.cumplido && (
                <BotonAccion disabled={trabajando} onClick={() => setModal({ tipo: 'saltar' })}>
                  <SkipForward size={15} /> Saltar este paso
                </BotonAccion>
              )}
              {destinosSalto.length > 0 && (
                <BotonAccion disabled={trabajando} onClick={() => setExpand(expand === 'saltar_a' ? null : 'saltar_a')}>
                  <SkipForward size={15} /> Saltar a...
                </BotonAccion>
              )}
            </div>
            {expand === 'saltar_a' && (
              <FormSaltarA
                pasos={destinosSalto}
                esCelular={esCelular}
                trabajando={trabajando}
                onConfirmar={saltarA}
                onCancelar={() => setExpand(null)}
              />
            )}
          </>
        )}

        {/* ===== PASO COMPLETADO: formulario ABIERTO y editable de entrada ===== */}
        {pv.situacion === 'hecho' && !esActualVisto && (
          <>
            {esPaso1 && capturaContexto}
            {esLevantamiento && (
              <LevantamientoForm
                valorInicial={levInicial}
                trabajando={trabajando}
                onGuardar={({ nota, datos }) => guardarRegistro('registrar_visita', { nota, datos, paso: pv.codigo })}
                onProgramarVisita={() => setProgramarVisita(true)}
              />
            )}
            {!esLevantamiento && (
              <BloqueRegistros registros={registros} trabajando={trabajando} onEditarRegistro={editarRegistro} fmtFH={fmtFH} puedeEditar={!bloqueado} verFinanzas={verFinanzas} />
            )}
            {esPagoReq && verFinanzas && <BloquePagos pagos={flujo.pagos} fmtFH={fmtFH} conCandado />}
            {!esPaso1 && !esLevantamiento && registros.length === 0 && !esPagoReq && !esCotReq && (
              <div style={{ fontSize: 13, color: COLOR_GRIS_CL, marginTop: 10 }}>
                Este paso se completo sin registros capturados.
              </div>
            )}
          </>
        )}

        {/* ===== PASO SALTADO: completar aqui mismo ===== */}
        {pv.situacion === 'saltado' && (
          <>
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
              padding: 12, marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8,
            }}>
              <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.45 }}>
                Este paso quedo pendiente por un brinco directo. Completa su informacion
                aqui mismo para que pase a verde.
              </div>
            </div>
            {esPaso1 && capturaContexto}
            {esLevantamiento && !bloqueado && (
              <LevantamientoForm
                valorInicial={levInicial}
                trabajando={trabajando}
                onGuardar={({ nota, datos }) => guardarRegistro('registrar_visita', { nota, datos, paso: pv.codigo })}
                onProgramarVisita={() => setProgramarVisita(true)}
              />
            )}
            {!esPaso1 && !esLevantamiento && accionRegistro && !bloqueado && (
              <FormRegistroInline
                accion={accionRegistro}
                pasoDestino={pv.codigo}
                trabajando={trabajando}
                sinCotizacion={!!(flujo && flujo.dinero && flujo.dinero.sin_cotizacion)}
                onGuardar={(datos) => guardarRegistro(accionRegistro, { ...datos, paso: pv.codigo })}
              />
            )}
            {!esPaso1 && !esLevantamiento && !accionRegistro && !esCotReq && (
              <div style={{ fontSize: 13, color: COLOR_GRIS, marginTop: 10, lineHeight: 1.5 }}>
                {esPagoReq
                  ? 'Este paso se completa registrando el pago real (candado de dinero): usa "Regresar a este paso".'
                  : 'Este paso se completa con su accion propia al regresar el expediente.'}
              </div>
            )}
            <BloqueRegistros registros={registros} trabajando={trabajando} onEditarRegistro={editarRegistro} fmtFH={fmtFH} puedeEditar={!bloqueado} verFinanzas={verFinanzas} />
          </>
        )}

        {/* ===== PASO FUTURO: preview + saltar aqui ===== */}
        {pv.situacion === 'pendiente' && !esActualVisto && (
          <>
            <div style={{ fontSize: 13, color: COLOR_GRIS, marginTop: 8, lineHeight: 1.5 }}>
              {(PASO_VISUAL[pv.codigo] && PASO_VISUAL[pv.codigo].descripcion) || 'Paso pendiente del flujo.'}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 8 }}>
              <Lock size={14} color={COLOR_GRIS_CL} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 12.5, color: COLOR_GRIS_CL, lineHeight: 1.45 }}>
                Pedira: {REQUISITO_TEXTO[pv.requisito] || 'completar su informacion'}.
              </div>
            </div>
            {puedeSaltarAqui && (
              <>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  <BotonAccion disabled={trabajando} onClick={() => setExpand(expand === 'saltar_aqui' ? null : 'saltar_aqui')}>
                    <SkipForward size={15} /> Saltar a este paso
                  </BotonAccion>
                </div>
                {expand === 'saltar_aqui' && (
                  <FormSaltarA
                    pasos={[pv]}
                    esCelular={esCelular}
                    trabajando={trabajando}
                    onConfirmar={saltarA}
                    onCancelar={() => setExpand(null)}
                  />
                )}
              </>
            )}
          </>
        )}

        {/* ===== NO APLICA ===== */}
        {pv.situacion === 'no_aplica' && (
          <div style={{ fontSize: 13, color: COLOR_GRIS_CL, marginTop: 8 }}>
            Este paso no aplica en este expediente (no hay faltantes de material).
          </div>
        )}

        {/* ===== ESPEJO-COTIZACIONES: en el paso Cotizacion (cualquier
            situacion) vive EL MISMO componente que la pestaña Cotizaciones —
            dashboard + lista con chips editables + escanear PDF. Misma fuente
            de datos: lo que cambies aqui se ve alla y viceversa. onCambio
            refresca el flujo (autorizar completa el paso solo, hook backend). */}
        {esCotReq && verFinanzas && (
          <div style={{ marginTop: 12, minWidth: 0 }}>
            {/* COTIZAR-RAPIDO: el boton va ARRIBA de la lista, que es donde el ojo ya
                esta cuando acaba de leer "Falta: vincular una cotizacion". */}
            <button
              onClick={() => setPanelCotizar(true)}
              style={{
                width: '100%', padding: '12px 16px', minHeight: 46, borderRadius: 10,
                border: 'none', background: '#1b9e6d', color: '#fff',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 12,
              }}
            >+ Cotizar ahora</button>
            <CotizacionesLista
              oportunidadId={expedienteId}
              onCambio={trasCambioCotizaciones}
            />
            {/* COTIZACIONES-PARECIDAS: sugerencias DEBAJO de las propias, plegadas */}
            <CotizacionesParecidas
              expedienteId={expedienteId}
              onCreada={trasCambioCotizaciones}
            />
            {panelCotizar && (
              <PanelCotizarRapido
                expedienteId={expedienteId}
                onCerrar={() => setPanelCotizar(false)}
                onCreada={trasCambioCotizaciones}
              />
            )}
          </div>
        )}
        {/* CALENDARIO-E1: al tecnico (sin finanzas) no se le muestra la lista de
            cotizaciones — la gestiona el area comercial. */}
        {esCotReq && !verFinanzas && (
          <div style={{ marginTop: 12, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5,
            background: '#f9fafb', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12 }}>
            La cotizacion de este expediente la gestiona el area comercial.
          </div>
        )}

        {/* Regresar (completados y saltados) — inline con motivo obligatorio */}
        {puedeRegresar && !esActualVisto && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                onClick={() => setExpand(expand === 'regresar' ? null : 'regresar')}
                disabled={trabajando}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: '#fff', color: '#b45309', border: '1px solid #f59e0b',
                  borderRadius: 10, padding: '10px 14px', fontSize: 13, fontWeight: 700,
                  fontFamily: FF, cursor: 'pointer', minHeight: 44,
                }}
              ><Undo2 size={15} /> Regresar a este paso</button>
            </div>
            {expand === 'regresar' && (
              <FormRegresar
                trabajando={trabajando}
                onConfirmar={(motivo) => regresarPaso(pv.codigo, motivo)}
                onCancelar={() => setExpand(null)}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const cambiarTipoTrabajo = async (tipo) => {
    try {
      const r = await llamar('PATCH', `/api/oportunidades/${expedienteId}/flujo/tipo-trabajo`, { tipo });
      setModal(null);
      if (r.sin_cambio) { avisar('El expediente ya esta en ese flujo'); return; }
      await cargar();
      if (typeof onActualizado === 'function') onActualizado();
      avisar(`Tipo de trabajo cambiado a ${r.workflow ? r.workflow.nombre : tipo}`);
    } catch (e) { avisar(e.message, 'error'); }
  };

  // ===== RENDER =====
  if (cargando) {
    return (
      <div style={est.caja}>
        <div style={est.skeletonFila}>
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} style={est.skeletonPaso} />)}
        </div>
        <div style={{ ...est.skeletonPaso, width: '60%', height: 44, borderRadius: 10, marginTop: 14 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={est.caja}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <AlertCircle size={18} color={COLOR_PELIGRO} />
          <span style={{ fontSize: 13, color: COLOR_GRIS, flex: 1, minWidth: 180 }}>
            No se pudo cargar el flujo del expediente.
          </span>
          <button onClick={() => { setCargando(true); cargar(); }} style={{ ...botonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={15} /> Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!flujo) return null;

  // Expediente cerrado por otra via (perdido/descartado): panel informativo.
  if (flujo.fuera_de_flujo && !flujo.cerrado) {
    return (
      <div style={est.caja}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <AlertCircle size={18} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5 }}>
            Este expediente esta en un estado fuera del flujo actual.
            {direccion
              ? ' Puedes reubicarlo cambiando el paso (badge de estado) o el tipo de trabajo.'
              : ' Pide a direccion que lo reubique en su paso.'}
          </div>
          {direccion && (
            <SelectorTipoTrabajo
              workflow={flujo.workflow || null}
              editable
              trabajando={trabajando}
              esCelular={esCelular}
              onElegir={(t) => setModal({ tipo: 'confirmar_tipo', destino: t })}
            />
          )}
        </div>
        {modal && modal.tipo === 'confirmar_tipo' && (
          <ModalConfirmar
            titulo="Cambiar tipo de trabajo"
            descripcion={`Cambiar de ${(flujo.workflow && flujo.workflow.nombre) || 'Sin flujo'} a ${modal.destino.label} re-organizara los pasos del flujo. Lo completado se conserva. ¿Continuar?`}
            etiquetaOk="Cambiar tipo"
            trabajando={trabajando}
            esCelular={esCelular}
            onCerrar={() => setModal(null)}
            onConfirmar={() => cambiarTipoTrabajo(modal.destino.codigo)}
          />
        )}
        {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
      </div>
    );
  }

  const actual = flujo.actual;
  const ev = actual ? actual.evaluacion : null;
  const flujoCompletado = flujo.cerrado && flujo.tipo_cierre === 'ganado';
  const pasosVisibles = flujo.pasos.filter(p => p.situacion !== 'no_aplica');

  // Progreso global (barra + %). TAREAS-HORIZONTAL: se quitaron las etapas
  // (Comercial/Material/...) y el checklist vertical; el flujo horizontal de
  // nodos comunica el avance.
  const pasosHechos = pasosVisibles.filter(p => p.situacion === 'hecho').length;
  const pctFlujo = pasosVisibles.length ? Math.round((pasosHechos / pasosVisibles.length) * 100) : 0;

  return (
    <div style={est.caja}>
      {/* ===== 1. CABECERA DE PROGRESO (estilo B) ===== */}
      <div style={{ marginBottom: 12 }}>
        {/* SELECTOR-TIPO (2026-07-11): la etiqueta "FLUJO: X" y el link
            "Cambiar tipo de trabajo" se unifican en UN dropdown (icono + tipo
            + flecha). Solo direccion puede cambiar (mismo candado soloDireccion
            del backend); para el resto es etiqueta de solo lectura. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLOR_GRIS_CL, textTransform: 'uppercase', letterSpacing: 0.6 }}>Flujo:</span>
          <SelectorTipoTrabajo
            workflow={flujo.workflow || null}
            editable={direccion && !flujo.cerrado}
            trabajando={trabajando}
            esCelular={esCelular}
            onElegir={(t) => setModal({ tipo: 'confirmar_tipo', destino: t })}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
          <div style={{ flex: 1, height: 8, background: '#e8ecf0', borderRadius: 99, overflow: 'hidden', minWidth: 80 }}>
            <div style={{
              height: '100%', width: `${pctFlujo}%`, background: COLOR_OK,
              borderRadius: 99, transition: 'width 0.3s ease-out',
            }} />
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: COLOR_OK, whiteSpace: 'nowrap' }}>{pctFlujo}%</span>
          <span style={{ fontSize: 12, color: COLOR_GRIS, whiteSpace: 'nowrap' }}>{pasosHechos} de {pasosVisibles.length} pasos</span>
        </div>
      </div>

      {/* TAREAS-HORIZONTAL (2026-07-13): se ELIMINARON las tarjetas de etapa
          (Comercial/Material/Ejecucion/Cierre) por pedido de Ricardo — el flujo
          horizontal de nodos de abajo ya comunica el avance. */}

      {/* Banners de estado global */}
      {flujoCompletado && (
        <div style={{ ...est.cajaPaso, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <PartyPopper size={20} color={COLOR_OK} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>Flujo completado</div>
              <div style={{ fontSize: 12.5, color: '#15803d', marginTop: 2 }}>
                Este expediente termino todos sus pasos y se cerro como ganado.
              </div>
            </div>
          </div>
        </div>
      )}
      {!flujoCompletado && flujo.cerrado && (
        <div style={{ ...est.cajaPaso, background: '#f9fafb' }}>
          <div style={{ fontSize: 13, color: COLOR_GRIS }}>
            El expediente esta cerrado; el flujo quedo pausado en "{actual ? actual.nombre : '—'}".
          </div>
        </div>
      )}

      {/* ===== FLUJO DE PASOS (TAREAS-HORIZONTAL, 2026-07-13): nodos en FILA
          horizontal con el nombre debajo (desktop/tablet) o chips deslizables
          (movil), conectores verde(recorrido)/gris(pendiente), nodo actual
          grande con halo naranja. Fuera el checklist vertical lateral. DEBAJO,
          el PANEL UNICO a todo el ancho con el contenido del paso. ===== */}
      {esCelular ? (
        /* ===== MOVIL-2 (2026-09-14): flujo tipo seguimiento de pedido =====
           Antes: una fila de chips con el NOMBRE de cada paso que se salia de la pantalla y habia
           que arrastrar; con 8-10 pasos no se entendia donde iba el expediente.
           Ahora, siguiendo la referencia de Ricardo (seguimiento de un pedido): POCOS elementos.
           - El paso ACTUAL, grande y con su nombre completo. Es la informacion principal.
           - Los demas, como iconos en UNA fila que CABE sin arrastrar.
           DECISION DE DISENO (consultada con Ricardo): con 8 o 10 pasos NO caben iconos de 44 px en
           360 px de ancho (darian 41 y 33). La fila muestra como MUCHO 5 elementos de 44 px (244 px
           de los 328 utiles): una pastilla "N hechos" que agrupa los completados, los 3 pasos
           siguientes, y una pastilla "+N" con el resto. */
        <FlujoMovil
          pasos={pasosVisibles}
          actual={actual}
          pasoVisto={pasoVisto}
          onVerPaso={verPaso}
          requisitoTexto={REQUISITO_TEXTO}
        />
      ) : (
        /* ===== FLUJO-CABE (2026-10-17): la fila de escritorio MIDE su ancho real y reparte los
           pasos para que SIEMPRE quepan todos a zoom 100%. Antes cada paso ocupaba 96 px fijos
           mas 28 px de conector: 10 pasos = 1,212 px, mas que lo que deja un monitor de 1366 con
           el menu lateral, y la barra de scroll "thin" no se veia: Facturacion y Cerrado parecian
           no existir hasta bajar el zoom al 80%. Si el ancho no alcanza para los nombres, se
           compacta a numeros dentro del nodo con el nombre del paso visto debajo; nunca se corta. */
        <FlujoEscritorio
          contRef={checklistRef}
          pasos={pasosVisibles}
          actual={actual}
          pasoVisto={pasoVisto}
          onVerPaso={verPaso}
        />
      )}
      {/* PANEL UNICO a todo el ancho, debajo del flujo */}
      <div ref={panelRef} style={{ width: '100%', minWidth: 0 }}>
        {renderPanelPaso()}
      </div>
      {modal && modal.tipo === 'checklist' && (
        <ModalChecklist
          solicitudId={flujo.solicitud.id}
          esCelular={esCelular}
          onCerrar={async (huboCambios) => {
            setModal(null);
            if (huboCambios) {
              await cargar();
              if (typeof onActualizado === 'function') onActualizado();
            }
          }}
          avisar={avisar}
        />
      )}
      {modal && modal.tipo === 'saltar' && (
        <ModalConfirmar
          titulo={`Saltar el paso "${actual ? actual.nombre : ''}"`}
          descripcion="El expediente pasara directo al siguiente paso. Quedara registrado quien lo salto y cuando."
          conNota
          placeholderNota="Por que se salta (opcional)..."
          etiquetaOk="Si, saltar"
          trabajando={trabajando}
          esCelular={esCelular}
          onCerrar={() => setModal(null)}
          onConfirmar={(nota) => saltarPaso(nota)}
        />
      )}
      {/* SELECTOR-TIPO: UNA confirmacion clara antes de re-mapear el flujo
          (dialogo permitido: confirma una operacion que reorganiza pasos). */}
      {modal && modal.tipo === 'confirmar_tipo' && (
        <ModalConfirmar
          titulo="Cambiar tipo de trabajo"
          descripcion={`Cambiar de ${(flujo.workflow && flujo.workflow.nombre) || 'Sin flujo'} a ${modal.destino.label} re-organizara los pasos del flujo. Lo completado se conserva. ¿Continuar?`}
          etiquetaOk="Cambiar tipo"
          trabajando={trabajando}
          esCelular={esCelular}
          onCerrar={() => setModal(null)}
          onConfirmar={() => cambiarTipoTrabajo(modal.destino.codigo)}
        />
      )}

      {programarVisita && (
        <ProgramarDesdeExpediente
          esCelular={esCelular}
          tipoSugerido="levantamiento"
          oportunidad={{
            id: expedienteId,
            folio: expediente && expediente.folio,
            titulo: expediente && expediente.titulo,
            cliente_nombre: expediente && (expediente.cliente_nombre || expediente.cliente_grupo_nombre),
          }}
          onCerrar={() => setProgramarVisita(false)}
          onCreada={() => {
            setProgramarVisita(false);
            setToast({ tipo: 'ok', msg: 'Visita programada. Ya aparece en el calendario.' });
            if (typeof onActualizado === 'function') onActualizado();
          }}
        />
      )}

      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </div>
  );
}

// ===== Sub-componentes =====

function BotonAccion({ primario, children, onClick, disabled }) {
  const [presionado, setPresionado] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onPointerDown={() => setPresionado(true)}
      onPointerUp={() => setPresionado(false)}
      onPointerLeave={() => setPresionado(false)}
      style={{
        ...(primario ? botonPrimario : botonSecundario),
        minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6,
        opacity: disabled ? 0.6 : 1,
        transform: presionado ? 'scale(0.97)' : 'scale(1)',
        transition: 'transform 0.12s ease-out, opacity 0.15s ease-out',
        WebkitTapHighlightColor: 'transparent',
      }}
    >{children}</button>
  );
}

function CabeceraModal({ titulo, onCerrar }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '16px 20px', borderBottom: `1px solid ${COLOR_BORDE}`,
    }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>{titulo}</div>
      <button onClick={onCerrar} aria-label="Cerrar" style={{
        background: 'transparent', border: 'none', fontSize: 22, color: COLOR_GRIS_CL,
        cursor: 'pointer', minWidth: 44, minHeight: 44, fontFamily: FF,
      }}>{'×'}</button>
    </div>
  );
}

function PieModal({ children }) {
  return (
    <div style={{
      padding: '14px 20px', borderTop: `1px solid ${COLOR_BORDE}`,
      display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap',
    }}>{children}</div>
  );
}

// Modal de checklist de material: TRAER DEL ALMACEN (salida real, FIFO por lotes) o marcar en falta.
// ALMACEN REARMADO (2026-10-19): ensena lo que hay en el almacen de cada linea; "Traer" = PATCH surtido
// (el servidor saca del inventario todo lo pendiente o contesta 409 con lo disponible).
function ModalChecklist({ solicitudId, esCelular, onCerrar, avisar }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCk, setErrorCk] = useState(null);
  const [guardandoItem, setGuardandoItem] = useState(null);
  const huboCambiosRef = useRef(false);

  const cargarSolicitud = useCallback(async () => {
    try {
      setErrorCk(null);
      const r = await fetch(`${API_BASE_CLI}/api/solicitudes-material/${solicitudId}`, { headers: authHeaders() })
        .then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'No se pudo cargar la solicitud');
      setDatos(r);
    } catch (e) {
      setErrorCk(e.message);
    } finally {
      setCargando(false);
    }
  }, [solicitudId]);

  useEffect(() => { cargarSolicitud(); }, [cargarSolicitud]);

  // SUB-ALMACENES (2026-10-19): traer del almacen CARGA el material a un tecnico (el material siempre tiene un dueno). Si el
  // servidor no puede decidir a quien (sin programacion y quien marca no es tecnico), contesta necesita_tecnico y aqui se
  // pinta un selector en la linea para elegirlo y volver a mandar.
  const [tecnicoPara, setTecnicoPara] = useState({}); // item id -> { tecnicos, elegido }
  const marcar = async (item, estadoLinea, tecnicoId) => {
    if (guardandoItem) return;
    setGuardandoItem(item.id);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/solicitudes-material/${solicitudId}/items/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ estado_linea: estadoLinea, ...(tecnicoId ? { tecnico_id: Number(tecnicoId) } : {}) }),
      }).then(x => x.json());
      if (!r.ok) {
        if (r.necesita_tecnico) setTecnicoPara(p => ({ ...p, [item.id]: { tecnicos: r.tecnicos || [], elegido: (r.sugeridos && r.sugeridos[0]) ? String(r.sugeridos[0]) : '' } }));
        throw new Error(r.error || 'No se pudo marcar');
      }
      setTecnicoPara(p => { const c = { ...p }; delete c[item.id]; return c; });
      huboCambiosRef.current = true;
      // Refresco local optimista con la respuesta.
      setDatos(prev => prev ? {
        ...prev,
        solicitud: { ...prev.solicitud, estado: r.estado_solicitud },
        items: prev.items.map(i => i.id === item.id ? r.item : i),
      } : prev);
      if (r.avances && r.avances.length > 0) {
        avisar(`El expediente avanzo al paso "${r.avances[r.avances.length - 1].a}"`);
      }
    } catch (e) {
      avisar(e.message, 'error');
    } finally {
      setGuardandoItem(null);
    }
  };

  const items = datos ? datos.items : [];
  const pendientes = items.filter(i => i.estado_linea === 'pendiente').length;
  const faltas = items.filter(i => i.estado_linea === 'falta').length;

  return (
    <ShellModal onClose={() => onCerrar(huboCambiosRef.current)} esCelular={esCelular} ancho={560}>
      <CabeceraModal
        titulo={datos ? `Checklist de material · ${datos.solicitud.folio}` : 'Checklist de material'}
        onCerrar={() => onCerrar(huboCambiosRef.current)}
      />
      <div style={{ padding: '14px 20px', overflowY: 'auto', flex: 1 }}>
        {cargando && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2, 3].map(i => <div key={i} style={{ height: 58, background: '#f3f4f6', borderRadius: 10 }} />)}
          </div>
        )}
        {errorCk && (
          <div style={{ padding: 14, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#b91c1c', fontSize: 13 }}>
            {errorCk}
            <button onClick={() => { setCargando(true); cargarSolicitud(); }} style={{ ...botonSecundario, minHeight: 44, marginLeft: 10 }}>Reintentar</button>
          </div>
        )}
        {!cargando && !errorCk && items.length === 0 && (
          <div style={{ textAlign: 'center', color: COLOR_GRIS_CL, padding: 24, fontSize: 13 }}>
            Esta solicitud no tiene articulos.
          </div>
        )}
        {!cargando && !errorCk && items.map(item => {
          const surtido = item.estado_linea === 'surtido';
          const falta = item.estado_linea === 'falta';
          // COMPRAS Y COSTO REAL (Parte 2): 'comprado' lo pone una compra confirmada; aqui no se toca.
          const comprado = item.estado_linea === 'comprado';
          const parcial = falta && Number(item.cantidad_comprada) > 0;
          // Traida EN PARTE del almacen: el boton Falta no es toggle (devolveria lo traido sin querer); se devuelve desde Material.
          const traidaEnParte = falta && Number(item.cantidad_surtida) > 0 && Number(item.cantidad_comprada) === 0;
          const ocupado = guardandoItem === item.id;
          return (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
              borderBottom: `1px solid #f3f4f6`, opacity: ocupado ? 0.6 : 1,
              transition: 'opacity 0.15s ease-out',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: COLOR_OSCURO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.nombre}
                </div>
                <div style={{ fontSize: 12, color: COLOR_GRIS_CL, marginTop: 2 }}>
                  {item.catalogo_modelo || item.referencia ? `${item.catalogo_modelo || item.referencia} · ` : ''}Cantidad: {Number(item.cantidad_solicitada)}
                  {parcial ? ` · ${Number(item.cantidad_comprada)} ya compradas` : ''}
                  {Number(item.cantidad_surtida) > 0 && Number(item.cantidad_pendiente_surtir) > 0 ? ` · ${Number(item.cantidad_surtida)} ya traídas` : ''}
                </div>
                {item.ligada_catalogo && !surtido && !comprado && (
                  <div style={{ fontSize: 12, marginTop: 2, fontWeight: 700, color: Number(item.existencia) >= Number(item.cantidad_pendiente_surtir) ? COLOR_OK : Number(item.existencia) > 0 ? '#b45309' : COLOR_PELIGRO }}>
                    {Number(item.existencia) >= Number(item.cantidad_pendiente_surtir) ? `✓ Hay ${Number(item.existencia)} en el almacén` : Number(item.existencia) > 0 ? `Solo hay ${Number(item.existencia)} en el almacén de ${Number(item.cantidad_pendiente_surtir)}` : 'No hay en el almacén: comprar'}
                  </div>
                )}
                {Array.isArray(item.en_tecnicos) && item.en_tecnicos.length > 0 && (
                  <div style={{ fontSize: 12, marginTop: 2, color: COLOR_GRIS }}>🪛 en manos de {item.en_tecnicos.map(t => `${t.nombre} (${Number(t.cantidad)})`).join(', ')}</div>
                )}
                {tecnicoPara[item.id] && (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>¿Quién se lleva el material?</span>
                    <select value={tecnicoPara[item.id].elegido} onChange={(e) => setTecnicoPara(p => ({ ...p, [item.id]: { ...p[item.id], elegido: e.target.value } }))} style={{ minHeight: 40, padding: '6px 8px', borderRadius: 8, border: '2px solid #b45309', fontFamily: FF, fontSize: 14 }}>
                      <option value="">Elige al técnico…</option>
                      {tecnicoPara[item.id].tecnicos.map(t => <option key={t.usuario_id} value={t.usuario_id}>{t.nombre}</option>)}
                    </select>
                    <button disabled={!tecnicoPara[item.id].elegido || !!guardandoItem} onClick={() => marcar(item, 'surtido', tecnicoPara[item.id].elegido)} style={{ minHeight: 40, padding: '6px 12px', borderRadius: 8, border: 'none', background: COLOR_OK, color: '#fff', fontWeight: 700, fontFamily: FF, cursor: 'pointer', opacity: tecnicoPara[item.id].elegido ? 1 : 0.5 }}>Traer y cargar</button>
                  </div>
                )}
              </div>
              {comprado ? (
                <span style={{ flexShrink: 0, minHeight: 44, display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 10, border: `2px solid ${COLOR_BEEP_AZUL}`, background: '#e6f1fb', color: COLOR_BEEP_AZUL, fontSize: 12.5, fontWeight: 700, fontFamily: FF }}>🛒 Comprado</span>
              ) : (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  onClick={() => marcar(item, surtido ? 'pendiente' : 'surtido')}
                  disabled={!!guardandoItem}
                  style={{
                    minHeight: 44, minWidth: 44, padding: '8px 12px', borderRadius: 10, fontFamily: FF,
                    border: surtido ? `2px solid ${COLOR_OK}` : `1px solid ${COLOR_BORDE}`,
                    background: surtido ? '#f0fdf4' : '#fff',
                    color: surtido ? '#166534' : COLOR_GRIS, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.15s ease-out',
                  }}
                >{surtido ? '✓ Traído' : (item.ligada_catalogo ? 'Traer del almacén' : 'Surtido')}</button>
                <button
                  onClick={() => marcar(item, falta ? 'pendiente' : 'falta')}
                  disabled={!!guardandoItem || traidaEnParte}
                  title={traidaEnParte ? 'Ya se trajo una parte del almacen; para devolverla usa la pestana Material' : undefined}
                  style={{
                    minHeight: 44, minWidth: 44, padding: '8px 12px', borderRadius: 10, fontFamily: FF,
                    border: falta ? `2px solid #b45309` : `1px solid ${COLOR_BORDE}`,
                    background: falta ? '#fffbeb' : '#fff',
                    color: falta ? '#92400e' : COLOR_GRIS, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.15s ease-out',
                  }}
                >{falta ? '✕ Falta' : 'Falta'}</button>
              </div>
              )}
            </div>
          );
        })}
      </div>
      {!cargando && !errorCk && items.length > 0 && (
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${COLOR_BORDE}`, fontSize: 12.5, color: COLOR_GRIS, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span>{items.length - pendientes - faltas} surtidos</span>
          <span style={{ color: faltas > 0 ? '#b45309' : COLOR_GRIS }}>{faltas} en falta</span>
          <span style={{ color: pendientes > 0 ? COLOR_BEEP_AZUL : COLOR_GRIS }}>{pendientes} por marcar</span>
        </div>
      )}
    </ShellModal>
  );
}

// Modal de confirmacion simple (saltar paso, faltantes recibidos).
function ModalConfirmar({ titulo, descripcion, conNota, placeholderNota, etiquetaOk, trabajando, esCelular, onCerrar, onConfirmar }) {
  const [nota, setNota] = useState('');
  return (
    <ShellModal onClose={trabajando ? () => {} : onCerrar} esCelular={esCelular} ancho={420}>
      <CabeceraModal titulo={titulo} onCerrar={trabajando ? () => {} : onCerrar} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13.5, color: COLOR_GRIS, lineHeight: 1.5 }}>{descripcion}</div>
        {conNota && (
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value.slice(0, 400))}
            placeholder={placeholderNota || 'Nota (opcional)...'}
            rows={2}
            style={{ ...inputStyle, minHeight: 66, resize: 'vertical' }}
          />
        )}
      </div>
      <PieModal>
        <button onClick={onCerrar} style={{ ...botonSecundario, minHeight: 44 }}>Cancelar</button>
        <button
          onClick={() => !trabajando && onConfirmar(nota.trim() || null)}
          disabled={trabajando}
          style={{ ...botonPrimario, minHeight: 44, opacity: trabajando ? 0.6 : 1 }}
        >{trabajando ? 'Guardando...' : (etiquetaOk || 'Confirmar')}</button>
      </PieModal>
    </ShellModal>
  );
}

// SELECTOR-TIPO (2026-07-11): dropdown de la cabecera que unifica la etiqueta
// "FLUJO: X" y el link "Cambiar tipo de trabajo". Muestra el tipo actual con
// su icono Lucide; si `editable` (direccion, expediente abierto) despliega los
// 3 tipos y onElegir dispara la confirmacion. Cierra con click fuera y Escape.
// El backend usa codigos de workflow (instalacion/reparaciones/ventas).
const OPCIONES_TIPO = [
  { codigo: 'instalacion',  ui: TIPOS_TRABAJO[0] },
  { codigo: 'reparaciones', ui: TIPOS_TRABAJO[1] },
  { codigo: 'ventas',       ui: TIPOS_TRABAJO[2] },
];

function SelectorTipoTrabajo({ workflow, editable = false, trabajando, esCelular, onElegir }) {
  const [abierto, setAbierto] = useState(false);
  const contRef = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    const onFuera = (e) => { if (contRef.current && !contRef.current.contains(e.target)) setAbierto(false); };
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', onFuera);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onFuera);
      document.removeEventListener('keydown', onKey);
    };
  }, [abierto]);

  const codigoActual = workflow ? workflow.codigo : null;
  const actual = OPCIONES_TIPO.find(o => o.codigo === codigoActual) || null;
  const IconoActual = actual ? actual.ui.Icono : AlertCircle;
  const colorActual = actual ? actual.ui.color : COLOR_GRIS_CL;
  const nombreActual = (workflow && workflow.nombre) || 'Sin flujo';

  return (
    <div ref={contRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => editable && !trabajando && setAbierto(a => !a)}
        disabled={!editable || trabajando}
        aria-haspopup={editable ? 'listbox' : undefined}
        aria-expanded={abierto}
        title={editable ? 'Cambiar tipo de trabajo' : `Tipo de trabajo: ${nombreActual}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '6px 12px', minHeight: 44, borderRadius: 10,
          border: `1px solid ${abierto ? colorActual : COLOR_BORDE}`,
          background: `${colorActual}0d`, fontFamily: FF,
          fontSize: 13, fontWeight: 700, color: COLOR_OSCURO,
          cursor: editable && !trabajando ? 'pointer' : 'default',
          opacity: trabajando ? 0.6 : 1,
        }}
      >
        <IconoActual size={16} color={colorActual} />
        {nombreActual}
        {editable && <ChevronDown size={15} color={COLOR_GRIS} strokeWidth={2.5}
          style={{ transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
      </button>

      {abierto && (
        <div role="listbox" style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 320,
          background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(0,0,0,0.16)', overflow: 'hidden',
          minWidth: esCelular ? 240 : 280, maxWidth: 'calc(100vw - 32px)',
        }}>
          {OPCIONES_TIPO.map(op => {
            const Icono = op.ui.Icono;
            const esActual = op.codigo === codigoActual;
            return (
              <button
                key={op.codigo}
                type="button"
                role="option"
                aria-selected={esActual}
                onClick={() => {
                  setAbierto(false);
                  if (!esActual) onElegir({ codigo: op.codigo, label: op.ui.label });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '10px 14px', minHeight: 48, textAlign: 'left', fontFamily: FF,
                  border: 'none', borderTop: op.codigo === 'instalacion' ? 'none' : `1px solid ${COLOR_BORDE}`,
                  background: esActual ? `${op.ui.color}11` : '#fff',
                  cursor: esActual ? 'default' : 'pointer',
                }}
              >
                <Icono size={17} color={op.ui.color} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: COLOR_OSCURO }}>{op.ui.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: COLOR_GRIS_CL, lineHeight: 1.3 }}>{op.ui.descripcion}</span>
                </span>
                {esActual && <Check size={15} color={op.ui.color} strokeWidth={3} style={{ flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// PANEL-NAVEGADOR: formularios y bloques INLINE del panel unico del paso.
// Reemplazan a los modales de registro/pago/salto/regreso (cero ventanas).
// ============================================

// Captura inline de un registro de paso (visita, falla, trabajo, entrega...).
//
// FACTURA-CON-IMPORTE (2026-09-28): cuando se factura un expediente que NO tiene
// ninguna cotizacion, se avisa ANTES de guardar y se ofrece capturar el importe
// en ese mismo momento. El 25 de septiembre se cerraron 23 expedientes asi, y
// hoy nadie sabe cuanto valieron: la unica huella es un folio de Aspel.
//
// El aviso NO bloquea (decision de Ricardo): "Continuar sin importe" siempre
// esta disponible. Si quien factura no puede terminar su trabajo dentro de Beep,
// lo hara fuera, y entonces no tendremos ni el folio.
function FormRegistroInline({ accion, pasoDestino, trabajando, onGuardar, sinCotizacion = false }) {
  const info = ACCIONES[accion] || {};
  const [nota, setNota] = useState('');
  const [folio, setFolio] = useState('');
  const [importe, setImporte] = useState('');
  const [error, setError] = useState(null);
  const notaOk = !info.notaObligatoria || nota.trim().length > 0;
  const folioOk = !info.pideFolio || folio.trim().length > 0;
  const valido = notaOk && folioOk && !trabajando;

  // El aviso solo tiene sentido en la factura y solo si no hay ningun precio
  // registrado en Beep. Con cotizacion, el valor ya esta guardado.
  const avisarImporte = !!info.pideFolio && sinCotizacion;
  const importeNum = Number(String(importe).replace(/[\s,$]/g, ''));
  const importeValido = importe.trim() !== '' && Number.isFinite(importeNum) && importeNum > 0;

  const guardar = async (conImporte) => {
    if (!valido) return;
    if (conImporte && !importeValido) {
      setError('Escribe el importe de la factura, o usa "Continuar sin importe".');
      return;
    }
    setError(null);
    try {
      await onGuardar({
        nota: nota.trim() || null,
        folio: folio.trim() || null,
        importe: conImporte ? importeNum : null,
        paso: pasoDestino || null,
      });
      setNota(''); setFolio(''); setImporte('');
    } catch (e) { setError(e.message || 'No se pudo guardar'); }
  };

  return (
    <div style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12, marginTop: 10, background: '#fff' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>
        {info.titulo || info.label}
      </div>
      {info.pideFolio && (
        <div style={{ marginBottom: 10 }}>
          <div style={etiquetaCampo}>Folio de la factura <span style={{ color: COLOR_PELIGRO }}>*</span></div>
          <input
            type="text"
            value={folio}
            onChange={(e) => setFolio(e.target.value.slice(0, 60))}
            placeholder="Ej: A-1234"
            style={{ ...inputStyle, minHeight: 44 }}
          />
        </div>
      )}

      {avisarImporte && (
        <div style={{
          border: '1px solid #f0d9a8', background: '#fdf7ec', borderRadius: 10,
          padding: 12, marginBottom: 10,
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
            <AlertCircle size={17} color="#b7791f" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 12.5, color: '#7a5b12', lineHeight: 1.45 }}>
              <strong>Este expediente no tiene cotizacion.</strong> Si lo facturas asi, no quedara
              registrado cuanto valio el trabajo.
            </div>
          </div>
          <div style={etiquetaCampo}>Importe de la factura</div>
          <input
            type="text"
            inputMode="decimal"
            value={importe}
            onChange={(e) => setImporte(e.target.value.slice(0, 16))}
            placeholder="Ej: 12500.00"
            style={{ ...inputStyle, minHeight: 44 }}
          />
          <div style={{ fontSize: 11.5, color: '#8a6d3b', marginTop: 6, lineHeight: 1.4 }}>
            Si no lo tienes a la mano, puedes continuar sin el y capturarlo despues.
          </div>
        </div>
      )}

      <textarea
        value={nota}
        onChange={(e) => setNota(e.target.value)}
        placeholder={info.placeholder || 'Notas (opcional)...'}
        rows={3}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 74, fontFamily: FF }}
      />
      {error && <div style={{ fontSize: 12, color: COLOR_PELIGRO, marginTop: 6 }}>{error}</div>}

      {/* En celular los dos botones se apilan: a 360 px no caben en una fila
          sin quedar por debajo del area tactil de 44 px. */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10,
        justifyContent: 'flex-end',
      }}>
        {avisarImporte && (
          <button
            onClick={() => guardar(false)}
            disabled={!valido}
            style={{
              ...botonSecundario, minHeight: 44, flex: '1 1 auto', minWidth: 150,
              opacity: valido ? 1 : 0.5, cursor: valido ? 'pointer' : 'not-allowed',
            }}
          >Continuar sin importe</button>
        )}
        <button
          onClick={() => guardar(avisarImporte)}
          disabled={!valido}
          style={{
            ...botonPrimario, minHeight: 44, flex: '1 1 auto', minWidth: 150,
            opacity: valido ? 1 : 0.5, cursor: valido ? 'pointer' : 'not-allowed',
          }}
        >{trabajando ? 'Guardando...' : (avisarImporte ? 'Registrar con importe' : (info.label || 'Guardar'))}</button>
      </div>
    </div>
  );
}

// Captura inline de un pago (mismo endpoint /pagos con TODOS sus candados).
function FormPagoInline({ sugerido, trabajando, onGuardar }) {
  const [tipo, setTipo] = useState('anticipo');
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('');
  const [nota, setNota] = useState('');
  const [error, setError] = useState(null);
  const montoNum = Number(monto);
  const valido = Number.isFinite(montoNum) && montoNum > 0 && !trabajando;

  const guardar = async () => {
    if (!valido) return;
    setError(null);
    try {
      await onGuardar({ tipo, monto: montoNum, metodo: metodo.trim() || null, nota: nota.trim() || null });
      setMonto(''); setMetodo(''); setNota('');
    } catch (e) { setError(e.message || 'No se pudo registrar el pago'); }
  };

  return (
    <div style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12, marginTop: 10, background: '#fff' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>Registrar pago</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {TIPOS_PAGO.map(t => (
          <button
            key={t.v}
            onClick={() => setTipo(t.v)}
            style={{
              padding: '10px 16px', borderRadius: 10, minHeight: 44,
              border: tipo === t.v ? `2px solid ${COLOR_BEEP_AZUL}` : `1px solid ${COLOR_BORDE}`,
              background: tipo === t.v ? '#eff6ff' : '#fff',
              color: tipo === t.v ? COLOR_BEEP_AZUL : COLOR_GRIS,
              fontWeight: tipo === t.v ? 700 : 500, fontSize: 14, fontFamily: FF, cursor: 'pointer',
              transition: 'all 0.15s ease-out',
            }}
          >{t.label}</button>
        ))}
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={etiquetaCampo}>Monto (MXN) <span style={{ color: COLOR_PELIGRO }}>*</span></div>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder={sugerido > 0 ? `Restante: $${fmtMonto(sugerido)}` : 'Ej: 15000'}
          style={{ ...inputStyle, minHeight: 44 }}
        />
        {sugerido > 0 && (
          <button
            onClick={() => setMonto(String(sugerido.toFixed(2)))}
            style={{ background: 'transparent', border: 'none', color: COLOR_BEEP_AZUL, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: FF, padding: '10px 0', minHeight: 44 }}
          >Usar el restante (${fmtMonto(sugerido)})</button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr' }}>
        <div>
          <div style={etiquetaCampo}>Metodo <span style={{ color: COLOR_GRIS_CL, fontWeight: 400 }}>(opcional)</span></div>
          <input type="text" value={metodo} onChange={(e) => setMetodo(e.target.value.slice(0, 30))}
            placeholder="Transferencia..." style={{ ...inputStyle, minHeight: 44 }} />
        </div>
        <div>
          <div style={etiquetaCampo}>Nota <span style={{ color: COLOR_GRIS_CL, fontWeight: 400 }}>(opcional)</span></div>
          <input type="text" value={nota} onChange={(e) => setNota(e.target.value.slice(0, 300))}
            placeholder="Referencia, banco..." style={{ ...inputStyle, minHeight: 44 }} />
        </div>
      </div>
      {error && <div style={{ fontSize: 12, color: COLOR_PELIGRO, marginTop: 6 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button
          onClick={guardar}
          disabled={!valido}
          style={{ ...botonPrimario, minHeight: 44, opacity: valido ? 1 : 0.5, cursor: valido ? 'pointer' : 'not-allowed' }}
        >{trabajando ? 'Guardando...' : 'Registrar pago'}</button>
      </div>
    </div>
  );
}

// Confirmacion inline de salto (destino unico o a elegir) con nota opcional.
function FormSaltarA({ pasos, esCelular, trabajando, onConfirmar, onCancelar }) {
  const [destino, setDestino] = useState((pasos[0] && pasos[0].codigo) || '');
  const [nota, setNota] = useState('');
  const valido = !!destino && !trabajando;
  return (
    <div style={{ border: '1px solid #bfdbfe', background: '#f8fbff', borderRadius: 10, padding: 12, marginTop: 10 }}>
      <div style={{ fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5, marginBottom: 10 }}>
        Los pasos que brinques quedaran en <strong style={{ color: '#b45309' }}>ambar</strong> como
        pendientes (se completan despues). El salto queda en el historial con tu nombre.
      </div>
      {pasos.length > 1 && (
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', marginBottom: 10 }}>
          {pasos.map(p => {
            const sel = destino === p.codigo;
            return (
              <button
                key={p.codigo}
                type="button"
                onClick={() => setDestino(p.codigo)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                  borderRadius: 10, minHeight: 44, textAlign: 'left', fontFamily: FF,
                  cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                  border: sel ? `2px solid ${COLOR_BEEP_AZUL}` : `1px solid ${COLOR_BORDE}`,
                  background: sel ? `${COLOR_BEEP_AZUL}14` : '#fff',
                  color: sel ? COLOR_BEEP_AZUL : COLOR_OSCURO,
                  fontSize: 13.5, fontWeight: 600,
                }}
              >
                <SkipForward size={14} style={{ flexShrink: 0, opacity: sel ? 1 : 0.4 }} />
                {p.nombre}
              </button>
            );
          })}
        </div>
      )}
      <input
        type="text"
        value={nota}
        onChange={(e) => setNota(e.target.value.slice(0, 300))}
        placeholder="Motivo (opcional). Ej: el cliente urge la cotizacion"
        style={{ ...inputStyle, minHeight: 44 }}
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={onCancelar} disabled={trabajando} style={{ ...botonSecundario, minHeight: 44 }}>Cancelar</button>
        <button
          onClick={() => valido && onConfirmar(destino, nota.trim() || null)}
          disabled={!valido}
          style={{ ...botonPrimario, minHeight: 44, opacity: valido ? 1 : 0.6 }}
        >{trabajando ? 'Saltando...' : 'Confirmar salto'}</button>
      </div>
    </div>
  );
}

// Confirmacion inline de REGRESO con motivo obligatorio (firmado).
function FormRegresar({ trabajando, onConfirmar, onCancelar }) {
  const [motivo, setMotivo] = useState('');
  const valido = motivo.trim().length >= 5 && !trabajando;
  return (
    <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10, padding: 12, marginTop: 10 }}>
      <div style={{ fontSize: 12.5, color: '#92400e', lineHeight: 1.5, marginBottom: 8 }}>
        El expediente volvera a este paso para rehacerlo. La informacion de pasos
        posteriores <strong>se conserva</strong> y el regreso queda en el historial con tu
        nombre y el motivo. Ojo: si el paso ya tenia informacion, el expediente puede
        volver a avanzar con lo registrado — corrige primero si vas a rehacer.
      </div>
      <div style={etiquetaCampo}>Motivo del regreso <span style={{ color: COLOR_PELIGRO }}>*</span></div>
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="Ej: la visita quedo incompleta, hay que volver a medir el sitio..."
        rows={2}
        style={{ ...inputStyle, resize: 'vertical', minHeight: 60, fontFamily: FF }}
        autoFocus
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={onCancelar} disabled={trabajando} style={{ ...botonSecundario, minHeight: 44 }}>Cancelar</button>
        <button
          onClick={() => valido && onConfirmar(motivo.trim())}
          disabled={!valido}
          style={{ ...botonPrimario, minHeight: 44, background: '#b45309', opacity: valido ? 1 : 0.6 }}
        >{trabajando ? 'Regresando...' : 'Regresar el expediente'}</button>
      </div>
    </div>
  );
}

// Registros capturados en un paso, con edicion inline y marca de supervision.
function BloqueRegistros({ registros, trabajando, onEditarRegistro, fmtFH, puedeEditar, verFinanzas = true }) {
  // PASOS-ABIERTOS (2026-07-13): cada registro editable se muestra con su campo
  // ABIERTO de entrada (sin boton "Editar" para desbloquear). draft[id] guarda
  // lo que el usuario escribe; si difiere de lo guardado, aparece "Guardar" +
  // aviso discreto "Cambios sin guardar". Al guardar, se limpia el draft y la
  // recarga silenciosa del padre trae el valor firmado (sin brinco).
  const [draft, setDraft] = useState({}); // { [id]: nota en edicion }
  const [errorId, setErrorId] = useState(null); // { id, msg }
  // RECUPERAR-IMPORTES (2026-09-28): importe que se esta capturando, por evento.
  const [impDraft, setImpDraft] = useState({});
  const [impError, setImpError] = useState(null);
  if (!registros || registros.length === 0) return null;

  const valorDe = (ev) => (draft[ev.id] !== undefined ? draft[ev.id] : (ev.nota || ''));
  const sucio = (ev) => draft[ev.id] !== undefined && draft[ev.id] !== (ev.nota || '');

  // ¿A esta factura le falta el importe? Se mira aunque el expediente este
  // CERRADO: los 149 que hay que recuperar lo estan, y capturar una cifra que
  // falta no cambia el expediente — no lo mueve de paso ni lo reabre.
  // Se esconde a quien no ve dinero: el servidor ya le manda el importe en
  // null, asi que sin esto le parecerian todas vacias.
  const faltaImporte = (ev) => (
    verFinanzas && ev.tipo === 'registro' && ev.registro_tipo === 'factura'
    && !(ev.datos && ev.datos.dinero_oculto)
    && !(ev.datos && Number(ev.datos.importe) > 0)
  );

  const guardarImporte = async (ev) => {
    const bruto = String(impDraft[ev.id] || '').replace(/[\s,$]/g, '');
    const n = Number(bruto);
    if (!bruto || !Number.isFinite(n) || n <= 0) {
      setImpError({ id: ev.id, msg: 'Escribe el importe de la factura (un numero mayor que cero).' });
      return;
    }
    setImpError(null);
    try {
      await onEditarRegistro(ev.id, null, n);
      setImpDraft(d => { const x = { ...d }; delete x[ev.id]; return x; });
    } catch (e) {
      setImpError({ id: ev.id, msg: e.message || 'No se pudo guardar el importe' });
    }
  };

  const guardar = async (ev) => {
    const val = valorDe(ev).trim();
    if (!val || trabajando) return;
    setErrorId(null);
    try {
      await onEditarRegistro(ev.id, val);
      // Limpia el draft: la recarga del padre traera la nota firmada.
      setDraft(d => { const n = { ...d }; delete n[ev.id]; return n; });
    } catch (e) {
      setErrorId({ id: ev.id, msg: e.message || 'No se pudo guardar la correccion' });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
      {registros.map(ev2 => {
        const editado = ev2.datos && ev2.datos.editado;
        const editable = puedeEditar && ev2.tipo === 'registro' && REGISTRO_LABELS[ev2.registro_tipo];
        const estaSucio = editable && sucio(ev2);
        const err = errorId && errorId.id === ev2.id ? errorId.msg : null;
        return (
          <div key={ev2.id} style={{ border: `1px solid ${estaSucio ? '#fcd9a8' : COLOR_BORDE}`, borderRadius: 10, padding: 12, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: COLOR_OSCURO }}>
                {ev2.tipo === 'saltado' ? 'Paso saltado'
                  : ev2.tipo === 'detour' ? 'Desvio a Compra'
                  : (REGISTRO_LABELS[ev2.registro_tipo] || ev2.registro_tipo)}
              </div>
              <div style={{ fontSize: 11.5, color: COLOR_GRIS_CL }}>
                {fmtFH(ev2.creado_at)}{ev2.usuario_nombre ? ` · ${ev2.usuario_nombre}` : ''}
              </div>
            </div>

            {/* RECUPERAR-IMPORTES (2026-09-28): capturar el importe de una
                factura que se registro sin el. Va FUERA del bloque de edicion a
                proposito: los 149 expedientes que hay que recuperar estan
                cerrados, y ahi la edicion de notas esta (bien) deshabilitada.
                Capturar una cifra que falta no cambia el expediente. */}
            {faltaImporte(ev2) && (
              <div style={{
                marginTop: 8, border: '1px solid #f0d9a8', background: '#fdf7ec',
                borderRadius: 10, padding: 10,
              }}>
                <div style={{ fontSize: 12, color: '#7a5b12', marginBottom: 7, lineHeight: 1.4 }}>
                  No quedo registrado cuanto valio esta factura. Si lo tienes a mano, capturalo.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={impDraft[ev2.id] || ''}
                    onChange={(e) => setImpDraft(d => ({ ...d, [ev2.id]: e.target.value.slice(0, 16) }))}
                    placeholder="Importe, ej: 12500.00"
                    style={{ ...inputStyle, minHeight: 44, flex: '1 1 160px', minWidth: 140 }}
                  />
                  <button
                    onClick={() => guardarImporte(ev2)}
                    disabled={trabajando || !String(impDraft[ev2.id] || '').trim()}
                    style={{
                      ...botonPrimario, minHeight: 44, flex: '0 1 auto',
                      opacity: (trabajando || !String(impDraft[ev2.id] || '').trim()) ? 0.6 : 1,
                    }}
                  ><Save size={15} /> {trabajando ? 'Guardando...' : 'Guardar importe'}</button>
                </div>
                {impError && impError.id === ev2.id && (
                  <div style={{ fontSize: 12, color: COLOR_PELIGRO, marginTop: 6 }}>{impError.msg}</div>
                )}
              </div>
            )}

            {verFinanzas && ev2.registro_tipo === 'factura' && ev2.datos && Number(ev2.datos.importe) > 0 && (
              <div style={{
                marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                padding: '6px 10px', fontSize: 12.5, color: '#1e40af', fontWeight: 600,
              }}>
                <CircleDollarSign size={14} />
                Importe: ${fmtMonto(ev2.datos.importe)}
                {ev2.datos.importe_capturado && ev2.datos.importe_capturado.retroactivo && (
                  <span style={{ fontWeight: 500, color: '#3b6fc4' }}>
                    · capturado despues{ev2.datos.importe_capturado.nombre ? ` por ${ev2.datos.importe_capturado.nombre}` : ''}
                  </span>
                )}
              </div>
            )}

            {editable ? (
              // Campo ABIERTO de entrada (guardar-al-cambiar).
              <div style={{ marginTop: 8 }}>
                <textarea
                  value={valorDe(ev2)}
                  onChange={(e) => setDraft(d => ({ ...d, [ev2.id]: e.target.value }))}
                  rows={3}
                  placeholder="Notas del paso..."
                  style={{ ...inputStyle, resize: 'vertical', minHeight: 70, fontFamily: FF }}
                />
                {err && <div style={{ fontSize: 12, color: COLOR_PELIGRO, marginTop: 6 }}>{err}</div>}
                {editado && (
                  <div style={{
                    marginTop: 6, fontSize: 11.5, color: '#b45309', background: '#fffbeb',
                    border: '1px solid #fde68a', borderRadius: 6, padding: '4px 8px', display: 'inline-block',
                  }}>
                    Editado despues de completado{editado.nombre ? ` por ${editado.nombre}` : ''} · {fmtFH(editado.fecha)}
                  </div>
                )}
                {estaSucio && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 600, marginRight: 'auto' }}>Cambios sin guardar</span>
                    <button onClick={() => setDraft(d => { const n = { ...d }; delete n[ev2.id]; return n; })} disabled={trabajando}
                      style={{ ...botonSecundario, minHeight: 44 }}>Descartar</button>
                    <button onClick={() => guardar(ev2)} disabled={trabajando || !valorDe(ev2).trim()}
                      style={{ ...botonPrimario, minHeight: 44, opacity: (trabajando || !valorDe(ev2).trim()) ? 0.6 : 1 }}>
                      <Save size={15} /> {trabajando ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // No editable (saltado/desvio/sistema): solo lectura.
              <>
                <div style={{ marginTop: 6, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {ev2.nota || <span style={{ color: COLOR_GRIS_CL, fontStyle: 'italic' }}>Sin notas</span>}
                </div>
                {editado && (
                  <div style={{
                    marginTop: 6, fontSize: 11.5, color: '#b45309', background: '#fffbeb',
                    border: '1px solid #fde68a', borderRadius: 6, padding: '4px 8px', display: 'inline-block',
                  }}>
                    Editado despues de completado{editado.nombre ? ` por ${editado.nombre}` : ''} · {fmtFH(editado.fecha)}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Pagos del expediente en SOLO lectura (los candados de dinero no se tocan).
function BloquePagos({ pagos, fmtFH, conCandado }) {
  return (
    <div style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12, marginTop: 10, background: '#fff' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GRIS_CL, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
        Pagos registrados
      </div>
      {(pagos || []).length === 0 ? (
        <div style={{ fontSize: 13, color: COLOR_GRIS_CL }}>Sin pagos registrados.</div>
      ) : (pagos || []).map(p2 => (
        <div key={p2.id} style={{ fontSize: 13, color: COLOR_OSCURO, marginBottom: 4 }}>
          {/* Importe de un pago de un expediente ajeno: se dice que el pago EXISTE y de que tipo
              es, pero no cuanto fue. "$0.00" seria falso. */}
          {p2.monto == null ? '' : `$${fmtMonto(p2.monto)} `}({p2.tipo}){p2.usuario_nombre ? ` · ${p2.usuario_nombre}` : ''} · {fmtFH(p2.creado_at)}
        </div>
      ))}
      {conCandado && (
        <div style={{ fontSize: 11.5, color: COLOR_GRIS_CL, marginTop: 6 }}>
          Los pagos no se editan desde aqui (candado de dinero).
        </div>
      )}
    </div>
  );
}

const etiquetaCampo = {
  fontSize: 12.5, fontWeight: 700, color: COLOR_GRIS, marginBottom: 6, fontFamily: FF,
};

const est = {
  caja: {
    background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 14,
    padding: 16, marginBottom: 18, fontFamily: FF,
  },
  cajaPaso: {
    border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
    padding: 14, marginTop: 10, background: '#fafafa',
  },
  skeletonFila: {
    display: 'flex', gap: 10, overflow: 'hidden',
  },
  skeletonPaso: {
    width: 64, height: 56, background: '#f3f4f6', borderRadius: 10, flexShrink: 0,
  },
};

// ============================================
// FLUJO EN CELULAR — MOVIL-2 (2026-09-14)
// ============================================
// Referencia de Ricardo: el seguimiento de un pedido. Pocos elementos, clarisimo.
// Antes esto era una fila de chips con el nombre de cada paso que se salia de la pantalla; con 8-10
// pasos habia que arrastrarla de lado y no se entendia donde iba el expediente.
//
// DECISION DE DISENO (consultada con Ricardo antes de construir): con 8 o 10 pasos NO caben iconos
// con area tactil de 44 px en una pantalla de 360 (darian 41 y 33 px). Por eso la fila muestra como
// MUCHO 5 elementos: la pastilla "N hechos" que agrupa los completados (desplegable), los 3 pasos
// siguientes, y una pastilla "+N" con el resto. 5 x 44 + 4 x 6 = 244 px de los 328 utiles.
// FLUJO-CABE (2026-10-17): fila horizontal de escritorio/tablet que CABE SIEMPRE.
// Mide el ancho real del contenedor (ResizeObserver) y reparte: si a cada paso le tocan
// al menos ANCHO_MIN_NOMBRE px, se pintan los nombres debajo de los nodos (modo
// "nombres"); si no, se pintan los nodos con su NUMERO dentro y debajo de la fila va
// "Paso N de M: <nombre>" del paso que se esta viendo (modo "compacto"). En ninguno de
// los dos hay scroll: la fila mide lo que mide el contenedor. Los tooltips llevan el
// nombre completo en ambos modos.
const ANCHO_MIN_NOMBRE = 74;   // "Levantamiento" a 10px mide ~68px y no se puede partir
const ANCHO_COMODO_NOMBRE = 84; // a partir de aqui el conector puede crecer (hasta 28 px)
const CONECTOR_MIN = 8;
const CONECTOR_MAX = 28;

function FlujoEscritorio({ contRef, pasos, actual, pasoVisto, onVerPaso }) {
  const [ancho, setAncho] = useState(0);

  useLayoutEffect(() => {
    const el = contRef && contRef.current;
    if (!el) return undefined;
    const medir = () => setAncho(el.clientWidth || 0);
    medir();
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(medir);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [contRef]);

  const n = pasos.length;
  const conectores = Math.max(0, n - 1);
  // Ancho que le toca a cada paso si los conectores van al minimo. Antes de la primera
  // medida (ancho 0) se asume que caben los nombres; useLayoutEffect corrige antes de pintar.
  // El conector mide lo que sobra despues de dar a cada paso un ancho comodo, entre 8 y 28 px.
  const conectorPx = conectores > 0 && ancho > 0
    ? Math.max(CONECTOR_MIN, Math.min(CONECTOR_MAX, Math.floor((ancho - n * ANCHO_COMODO_NOMBRE) / conectores)))
    : CONECTOR_MIN;
  const porPaso = ancho > 0 && n > 0 ? (ancho - conectores * conectorPx) / n : ANCHO_MIN_NOMBRE;
  const compacto = porPaso < ANCHO_MIN_NOMBRE;
  // Entre 74 y 91 px por paso (p. ej. 11 pasos en un monitor de 1366) la letra baja a 10 px para
  // que 'Levantamiento' quepa en una linea; overflowWrap queda solo como red por si acaso.
  const fuenteNombre = porPaso >= 92 ? 11 : 10;
  const visto = pasos.find(p => p.codigo === pasoVisto) || actual || pasos[0];
  const idxVisto = visto ? pasos.indexOf(visto) : -1;
  const idxActual = actual ? pasos.findIndex(p => p.codigo === actual.codigo) : -1;
  const faltan = idxActual >= 0 ? Math.max(0, n - idxActual - 1) : 0;

  // Nodo: en modo nombres igual que siempre (palomita / alerta / actual con halo / vacio);
  // en compacto, el NUMERO del paso dentro del circulo para que se cuente de un vistazo.
  const nodo = (p, idx, esActual, hecho, saltado) => {
    const num = idx + 1;
    if (hecho) {
      return <span style={{ width: 26, height: 26, borderRadius: '50%', background: COLOR_OK, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{compacto ? num : <Check size={14} strokeWidth={3} />}</span>;
    }
    if (saltado) {
      return <span style={{ width: 26, height: 26, borderRadius: '50%', background: '#fffbeb', border: '2px solid #f59e0b', color: '#b45309', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 }}>{compacto ? num : <AlertTriangle size={14} />}</span>;
    }
    if (esActual) {
      return <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#fff7ed', border: '2px solid #f97316', color: '#c2410c', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 4px rgba(249,115,22,0.22)', fontSize: 13, fontWeight: 800 }}>{compacto ? num : <CircleDot size={18} />}</span>;
    }
    return <span style={{ width: compacto ? 26 : 22, height: compacto ? 26 : 22, borderRadius: '50%', background: '#fff', border: '2px solid #d1d5db', color: COLOR_GRIS_CL, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{compacto ? num : null}</span>;
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        ref={contRef}
        role="list"
        aria-label={`Pasos del flujo, ${n} en total`}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 0, width: '100%', minWidth: 0, paddingBottom: compacto ? 2 : 6 }}
      >
        {pasos.map((p, idx) => {
          const esActual = p.situacion === 'actual';
          const hecho = p.situacion === 'hecho';
          const saltado = p.situacion === 'saltado';
          const completado = hecho || saltado;
          const esVisto = p.codigo === pasoVisto;
          const ultimo = idx === n - 1;
          return (
            <React.Fragment key={p.codigo}>
              <button
                type="button"
                role="listitem"
                data-paso={p.codigo}
                data-actual={esActual ? '1' : undefined}
                onClick={() => onVerPaso(p.codigo)}
                title={`${idx + 1} de ${n}: ${p.nombre}`}
                aria-label={`Paso ${idx + 1} de ${n}: ${p.nombre}`}
                aria-current={esActual ? 'step' : undefined}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                  // flex 1 1 0 + minWidth 0: la fila se reparte el ancho, nunca se desborda
                  flex: '1 1 0', minWidth: 0, minHeight: 44, padding: '4px 2px', border: 'none', borderRadius: 10,
                  background: esVisto ? '#eef4fb' : 'transparent', cursor: 'pointer',
                  fontFamily: FF, WebkitTapHighlightColor: 'transparent',
                }}
              >
                {/* NODO (altura fija 34 para alinear los conectores) */}
                <span style={{ height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {nodo(p, idx, esActual, hecho, saltado)}
                </span>
                {/* NOMBRE debajo del nodo (hasta 2 lineas, centrado), solo si hay sitio */}
                {!compacto && (
                  <span style={{
                    fontSize: fuenteNombre, lineHeight: 1.2, textAlign: 'center', width: '100%',
                    fontWeight: (esActual || esVisto) ? 700 : 500,
                    color: saltado ? '#b45309' : esActual ? '#c2410c' : hecho ? COLOR_GRIS : COLOR_GRIS_CL,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    overflowWrap: 'anywhere',
                  }}>{p.nombre}</span>
                )}
              </button>
              {/* CONECTOR al siguiente nodo: ancho calculado arriba (8..28 px) */}
              {!ultimo && (
                <span aria-hidden="true" style={{
                  flex: '0 0 auto', width: compacto ? Math.min(conectorPx, 24) : conectorPx, height: 3, borderRadius: 2,
                  marginTop: 20, background: completado ? COLOR_OK : '#d1d5db',
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* En compacto los nombres no estan en la fila: aqui va el del paso que se ve y donde va el expediente */}
      {compacto && visto && (
        <div style={{ fontSize: 12.5, color: COLOR_GRIS, marginTop: 6, lineHeight: 1.4 }}>
          <b style={{ color: idxVisto === idxActual ? '#c2410c' : COLOR_OSCURO }}>Paso {idxVisto + 1} de {n}: {visto.nombre}</b>
          {idxActual >= 0 && idxVisto !== idxActual && (
            <span> {'\u00b7'} el expediente va en el {idxActual + 1} ({actual.nombre})</span>
          )}
          {idxActual >= 0 && (
            <span> {'\u00b7'} {faltan > 0 ? `${faltan === 1 ? 'falta 1 paso' : `faltan ${faltan} pasos`} para cerrar` : 'es el ultimo paso'}</span>
          )}
        </div>
      )}
    </div>
  );
}

function FlujoMovil({ pasos, actual, pasoVisto, onVerPaso, requisitoTexto }) {
  const [verHechos, setVerHechos] = useState(false);
  // FLUJO-CABE (2026-10-17): la pastilla "+N" tambien se despliega; ningun paso queda invisible.
  const [verTodos, setVerTodos] = useState(false);
  const idxActual = Math.max(0, pasos.findIndex(p => p.situacion === 'actual'));
  const paso = pasos.find(p => p.codigo === (pasoVisto || (actual && actual.codigo))) || pasos[idxActual] || pasos[0];
  if (!paso) return null;

  const hechos = pasos.filter(p => p.situacion === 'hecho' || p.situacion === 'saltado');
  const pendientes = pasos.filter(p => p.situacion === 'pendiente');
  const MAX_PENDIENTES = 3;
  const visiblesPend = verTodos ? pendientes : pendientes.slice(0, MAX_PENDIENTES);
  const restantes = pendientes.length - visiblesPend.length;
  // Cuantos pasos faltan DESPUES del actual del expediente (no del que se esta viendo).
  const faltan = Math.max(0, pasos.length - idxActual - 1);

  const visualDe = (p) => PASO_VISUAL[p.codigo] || { color: COLOR_GRIS, label: p.nombre, icono: 'Circle' };
  const vActual = visualDe(paso);
  const requisito = paso.requisito ? (requisitoTexto[paso.requisito] || paso.requisito) : null;

  const boton = (p, estado) => {
    const v = visualDe(p);
    const esVisto = p.codigo === pasoVisto;
    const col = estado === 'hecho' ? COLOR_OK : estado === 'actual' ? '#c2410c' : '#9aa0a6';
    const bg = estado === 'hecho' ? '#f0fdf4' : estado === 'actual' ? '#fff7ed' : '#f7f8f9';
    return (
      <button
        key={p.codigo}
        type="button"
        onClick={() => onVerPaso(p.codigo)}
        title={v.label || p.nombre}
        aria-label={v.label || p.nombre}
        style={{
          width: 44, height: 44, flexShrink: 0, borderRadius: 12,
          border: esVisto ? `2px solid ${COLOR_OSCURO}` : `1px solid ${estado === 'hecho' ? '#bbf7d0' : COLOR_BORDE}`,
          background: bg, color: col, display: 'grid', placeItems: 'center',
          cursor: 'pointer', WebkitTapHighlightColor: 'transparent', padding: 0,
        }}
      >
        <IconoLucide nombre={v.icono} size={19} />
      </button>
    );
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {/* B1 — EL PASO ACTUAL, grande y con su nombre completo. Es lo principal. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
        background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderLeft: `4px solid ${vActual.color}`,
        borderRadius: 12, marginBottom: 10,
      }}>
        <div style={{
          width: 46, height: 46, flexShrink: 0, borderRadius: 14,
          background: `${vActual.color}1a`, color: vActual.color, display: 'grid', placeItems: 'center',
        }}>
          <IconoLucide nombre={vActual.icono} size={24} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16.5, fontWeight: 800, color: COLOR_OSCURO, lineHeight: 1.2 }}>
            {vActual.label || paso.nombre}
          </div>
          {/* B3 — "Paso N de M" y, en una linea, QUE FALTA para avanzar. */}
          <div style={{ fontSize: 11.5, color: COLOR_GRIS_CL, marginTop: 3 }}>
            Paso {pasos.indexOf(paso) + 1} de {pasos.length}
            {faltan > 0 ? ` \u00b7 ${faltan === 1 ? 'falta 1 paso' : `faltan ${faltan} pasos`}` : ' \u00b7 es el ultimo paso'}
          </div>
        </div>
      </div>
      {requisito && (
        <div style={{
          fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: 10, padding: '8px 11px', marginBottom: 10, lineHeight: 1.45,
        }}>
          <b>Falta:</b> {requisito}
        </div>
      )}

      {/* B2 — los demas pasos como iconos, en UNA fila que cabe sin arrastrar. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {hechos.length > 0 && !verHechos && (
          <button
            type="button"
            onClick={() => setVerHechos(true)}
            style={{
              height: 44, minWidth: 44, flexShrink: 0, padding: '0 12px', borderRadius: 12,
              border: '1px solid #bbf7d0', background: '#f0fdf4', color: COLOR_OK,
              fontFamily: FF, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <Check size={15} strokeWidth={3} /> {hechos.length} hecho{hechos.length === 1 ? '' : 's'}
          </button>
        )}
        {verHechos && hechos.map(p => boton(p, 'hecho'))}
        {verHechos && (
          <button
            type="button" onClick={() => setVerHechos(false)}
            style={{
              height: 44, minWidth: 44, padding: '0 10px', borderRadius: 12, border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', color: COLOR_GRIS_CL, fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >Ocultar</button>
        )}
        {visiblesPend.map(p => boton(p, 'pendiente'))}
        {restantes > 0 && (
          <button
            type="button" onClick={() => setVerTodos(true)}
            title={`Ver los ${restantes} pasos que faltan`}
            aria-label={`Ver los ${restantes} pasos que faltan`}
            style={{
              height: 44, minWidth: 44, padding: '0 10px', borderRadius: 12, border: `1px dashed ${COLOR_BORDE}`,
              background: '#fff', color: COLOR_GRIS_CL, fontFamily: FF, fontSize: 12, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >+{restantes}</button>
        )}
        {verTodos && pendientes.length > MAX_PENDIENTES && (
          <button
            type="button" onClick={() => setVerTodos(false)}
            style={{
              height: 44, minWidth: 44, padding: '0 10px', borderRadius: 12, border: `1px solid ${COLOR_BORDE}`,
              background: '#fff', color: COLOR_GRIS_CL, fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >Menos</button>
        )}
      </div>
    </div>
  );
}
